import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import { FabricImage } from 'fabric'
import SheetCanvas, { BASE_PPI, SAFETY_MARGIN_OBJECT_NAME } from './SheetCanvas'
import { resolveThemeColor, withAlpha, SHEET_SURFACE_COLOR } from './themeColors'
import ZoomControls from './ZoomControls'
import SheetRuler from './SheetRuler'
import Sidebar from './Sidebar'
import { deleteUpload, fetchImageUrl } from './api'
import { useAppArtworkUrls } from './useAppArtworkUrls'
import { loadArtworkImage } from './loadArtworkImage'
import { useUndoRedo } from './useUndoRedo'
import { getCanvasState, applyCanvasState } from './canvasState'
import { getPlacedObjectSizeInches, serializeLayout } from './layoutSerializer'
import { autofill, placementAnchorCoordinates } from './autofill'
import { findCopyBounds } from './copyPlacement'
import SelectionToolbar from './SelectionToolbar'
import BuilderAlert from './BuilderAlert'
import { ARTWORK_QUALITY_WARNING_COPY, buildArtworkQualityWarnings } from './artworkQuality'
import { calculateDynamicPricing } from './dynamicPricing'
import { firstOverlappingSheetNumber } from './canvasTransforms'
import { sheetWidthPolicy } from './sheetWidthPolicy'
import { calculateFitZoom } from './fitZoom'
import useCompactEditor from './useCompactEditor'
import { imagesArePending } from './draftState'
import { buildConsolidationAdvisory, consolidationAdvisoryCopy, CONSOLIDATION_ADVISORY_TITLE } from './consolidationAdvisory'
import {
  MAX_ARTWORK_QUANTITY,
  countPlacedCopies,
  effectiveRequestedQuantity,
  filterLayoutToIncludedSources,
  normalizeArtworkConfig,
  planArtworkPlacements,
  reconcileRequestedQuantityAfterAdd,
  reconcileRequestedQuantityAfterPlacement,
  reconcileRequestedQuantityAfterRemove,
} from './artworkSource'
import './editor.css'

const SAFETY_MARGIN_IN = 0.125
const BOUNDS_EPSILON_PX = 0.5
const COPY_PLACEMENT_GAP_PX = SAFETY_MARGIN_IN * BASE_PPI
const REVIEW_PREVIEW_MAX_WIDTH_PX = 720
const REVIEW_PREVIEW_MAX_HEIGHT_PX = 640
const REVIEW_PREVIEW_TALL_MAX_HEIGHT_PX = 860
const REVIEW_PREVIEW_MIN_SCALE = 0.5
const REVIEW_PREVIEW_MAX_SCALE = 1.0
const ZOOM_EPSILON = 0.0001
const RESTORE_NOT_STARTED = 'not_started'
const RESTORE_RESTORING = 'restoring'
const RESTORE_COMPLETE = 'complete'

function initialSheetDefinitions(layout) {
  const restoredSheets = layout?.sheets || []
  const count = Math.max(restoredSheets.length, 1)

  return Array.from({ length: count }, (_, index) => ({
    id: crypto.randomUUID(),
    label: `Sheet ${index + 1}`,
    ...(restoredSheets[index]?.width_in != null && { width_in: restoredSheets[index].width_in }),
    ...(restoredSheets[index]?.height_in != null && { height_in: restoredSheets[index].height_in }),
  }))
}

export function getReviewThumbnailScale(sheetSize, canvasWidthPx, canvasHeightPx) {
  const isTallSheet = sheetSize.height_in / sheetSize.width_in >= 2
  const targetHeight = isTallSheet ? REVIEW_PREVIEW_TALL_MAX_HEIGHT_PX : REVIEW_PREVIEW_MAX_HEIGHT_PX
  const displayScale = Math.min(
    REVIEW_PREVIEW_MAX_WIDTH_PX / canvasWidthPx,
    targetHeight / canvasHeightPx
  )

  return Math.min(REVIEW_PREVIEW_MAX_SCALE, Math.max(REVIEW_PREVIEW_MIN_SCALE, displayScale))
}

function getObjectBounds(obj) {
  obj.setCoords?.()
  if (typeof obj.getCoords === 'function') {
    const coords = obj.getCoords()
    if (coords?.length) {
      const xs = coords.map(point => point.x)
      const ys = coords.map(point => point.y)
      const left = Math.min(...xs)
      const top = Math.min(...ys)
      return {
        left,
        top,
        width: Math.max(...xs) - left,
        height: Math.max(...ys) - top,
      }
    }
  }

  if (typeof obj.getBoundingRect === 'function') return obj.getBoundingRect()

  return {
    left: obj.left || 0,
    top: obj.top || 0,
    width: (obj.width || 0) * (obj.scaleX || 1),
    height: (obj.height || 0) * (obj.scaleY || 1),
  }
}

function bringSafetyMarginToFront(canvas) {
  if (typeof canvas?.getObjects !== 'function') return
  const safetyMargin = canvas.getObjects().find(o => o.name === SAFETY_MARGIN_OBJECT_NAME)
  if (safetyMargin) canvas.bringObjectToFront?.(safetyMargin)
}

function activateCanvasObject(canvas, object) {
  if (
    !canvas ||
    !object ||
    canvas.selection === false ||
    object.selectable === false ||
    object.evented === false
  ) {
    canvas?.discardActiveObject?.()
    return false
  }

  canvas.setActiveObject?.(object)
  return canvas.getActiveObject?.() === object || typeof canvas.getActiveObject !== 'function'
}

function selectionReferencesUploadedImage(selection, uploadedImageId) {
  if (!selection) return false
  if (String(selection.uploadedImageId) === uploadedImageId) return true

  return selection.type === 'activeSelection' &&
    selection.getObjects?.().some(object => String(object.uploadedImageId) === uploadedImageId)
}

function selectionReferencesAnyObject(selection, objects) {
  if (!selection) return false
  if (objects.has(selection)) return true

  return selection.type === 'activeSelection' &&
    selection.getObjects?.().some(object => objects.has(object))
}

function drawSafetyMarginOverlay(canvasEl, sheetSize, scale) {
  const ctx = canvasEl.getContext?.('2d')
  if (!ctx) return

  const horizontalMargin = Number(
    sheetSize.horizontal_safety_margin_in ?? SAFETY_MARGIN_IN
  ) * BASE_PPI * scale
  const verticalMargin = SAFETY_MARGIN_IN * BASE_PPI * scale
  const width = sheetSize.width_in * BASE_PPI * scale
  const height = sheetSize.height_in * BASE_PPI * scale

  ctx.save()
  ctx.strokeStyle = withAlpha(resolveThemeColor('--color-danger'), 0.9)
  ctx.lineWidth = 1
  ctx.setLineDash([Math.max(2, 6 * scale), Math.max(2, 4 * scale)])
  ctx.strokeRect(
    horizontalMargin,
    verticalMargin,
    width - horizontalMargin * 2,
    height - verticalMargin * 2
  )
  ctx.restore()
}

// imagePool: Map<string, FabricImage> — source assets, never placed directly.
// imagePool keys are always strings — normalized at write boundary.
// dataTransfer.getData() returns strings, so reads match without casting.
//
// placeImage clones from the pool onto the canvas at the given position/size.
// onPlaced: optional callback fired after canvas.add() — used by callers that need to
// track async completion (e.g. autofill, undo restore).
function configureImageClone(clone, uploadedImageId, dropX, dropY, printWidthIn, printHeightIn, options) {
  const widthPx  = printWidthIn  * BASE_PPI
  const heightPx = printHeightIn * BASE_PPI
  clone.set({
    left:            dropX,
    top:             dropY,
    originX:         'left',
    originY:         'top',
    scaleX:          widthPx  / clone.width,
    scaleY:          heightPx / clone.height,
    uploadedImageId: uploadedImageId,
    objectId:        options?.objectId || crypto.randomUUID(),
    quantity:        1,
    ...(options?.angle != null && { angle: options.angle }),
    ...(options?.flipX != null && { flipX: options.flipX }),
    ...(options?.flipY != null && { flipY: options.flipY }),
  })
  clone.setCoords?.()
  return clone
}

function prepareImageClone(imagePool, uploadedImageId, dropX, dropY, printWidthIn, printHeightIn, options) {
  if (!printWidthIn || !printHeightIn || printWidthIn <= 0 || printHeightIn <= 0) {
    return Promise.resolve(null)
  }
  const source = imagePool.get(String(uploadedImageId))
  if (!source) {
    return Promise.resolve(null)
  }
  try {
    return Promise.resolve(source.clone())
      .then(clone => configureImageClone(
        clone,
        uploadedImageId,
        dropX,
        dropY,
        printWidthIn,
        printHeightIn,
        options
      ))
      .catch(() => null)
  } catch {
    return Promise.resolve(null)
  }
}

export function placeImage(imagePool, uploadedImageId, canvas, dropX, dropY, printWidthIn, printHeightIn, onPlaced, options) {
  if (!canvas || !printWidthIn || !printHeightIn || printWidthIn <= 0 || printHeightIn <= 0) {
    return Promise.resolve(null)
  }
  const source = imagePool.get(String(uploadedImageId))
  if (!source) return Promise.resolve(null)

  let clonePromise
  try {
    clonePromise = source.clone()
  } catch {
    return Promise.resolve(null)
  }
  return Promise.resolve(clonePromise).then(rawClone => {
    const clone = configureImageClone(
      rawClone,
      uploadedImageId,
      dropX,
      dropY,
      printWidthIn,
      printHeightIn,
      options
    )
    if (options?.shouldPlace && !options.shouldPlace()) {
      onPlaced?.(null)
      return null
    }

    canvas.add(clone)
    if (options?.activate !== false) {
      activateCanvasObject(canvas, clone)
    }
    bringSafetyMarginToFront(canvas)
    canvas.requestRenderAll?.()
    onPlaced?.(clone)
    return clone
  }).catch(() => {
    onPlaced?.(null)
    return null
  })
}

export function refreshPlacedImageElements(canvases, uploadedImageId, sourceImage) {
  const sourceElement = sourceImage?.getElement?.()
  if (!sourceElement) return 0

  let refreshed = 0
  ;(canvases || []).forEach(canvas => {
    if (typeof canvas?.getObjects !== 'function') return

    const matchingObjects = canvas.getObjects().filter(object => (
      isSelectableCanvasObject(object) &&
      String(object.uploadedImageId) === String(uploadedImageId) &&
      typeof object.setElement === 'function'
    ))
    matchingObjects.forEach(object => {
      const renderedWidth = object.width * (object.scaleX || 1)
      const renderedHeight = object.height * (object.scaleY || 1)
      object.setElement(sourceElement)
      object.set({
        scaleX: object.width ? renderedWidth / object.width : object.scaleX,
        scaleY: object.height ? renderedHeight / object.height : object.scaleY,
      })
      object.setCoords?.()
      refreshed += 1
    })
    if (matchingObjects.length > 0) canvas.requestRenderAll?.()
  })

  return refreshed
}

export function buildAutoArrangePlan(images, placedObjects, createInstanceId = () => crypto.randomUUID()) {
  const reconciliation = planArtworkPlacements(images, placedObjects, createInstanceId)
  const retainedItems = reconciliation.retained.map(({ source, object, instanceId }) => {
    const size = getPlacedObjectSizeInches(object)

    return {
      id: source.id,
      uploaded_image_id: source.id,
      instance_id: instanceId,
      width_in: size.widthIn,
      height_in: size.heightIn,
      quantity: 1,
      rotation_deg: Number(object.angle) || 0,
      flip_x: Boolean(object.flipX),
      flip_y: Boolean(object.flipY),
    }
  })
  const missingItems = reconciliation.missing.map(({ source, instanceId }) => ({
    id: source.id,
    uploaded_image_id: source.id,
    instance_id: instanceId,
    width_in: source.printWidthIn,
    height_in: source.printHeightIn,
    quantity: 1,
    rotation_deg: 0,
    flip_x: false,
    flip_y: false,
  }))

  return { ...reconciliation, items: [...retainedItems, ...missingItems] }
}

// Fresh-pack: ignore current canvas state for packing AND remove all of it.
// Two independent concerns — an empty placed-objects input alone yields empty
// removals, which would leave stale objects alive beside the fresh clones
// (the atomic batch removes only objects listed in `removals`).
export function buildFreshPackPlan(images, placedObjects, createInstanceId = () => crypto.randomUUID()) {
  const plan = buildAutoArrangePlan(images, [], createInstanceId)
  return {
    ...plan,
    retained: [],
    removals: (placedObjects || []).map(object => ({ object })),
  }
}

function checkBounds(canvas, sheetSize) {
  const horizontalSafetyPx = Number(
    sheetSize.horizontal_safety_margin_in ?? SAFETY_MARGIN_IN
  ) * BASE_PPI
  const verticalSafetyPx = SAFETY_MARGIN_IN * BASE_PPI
  const maximumArtworkWidthPx = Number(
    sheetSize.nominal_width_in ?? sheetSize.width_in
  ) * BASE_PPI
  const objects = canvas.getObjects().filter(o => o.name !== SAFETY_MARGIN_OBJECT_NAME)
  let outsideSafety = false
  let outsideSheet  = false
  objects.forEach(obj => {
    const bounds = getObjectBounds(obj)
    if (
      bounds.left < horizontalSafetyPx - BOUNDS_EPSILON_PX ||
      bounds.top  < verticalSafetyPx - BOUNDS_EPSILON_PX ||
      bounds.left + bounds.width  > sheetSize.width_in  * BASE_PPI - horizontalSafetyPx + BOUNDS_EPSILON_PX ||
      bounds.top  + bounds.height > sheetSize.height_in * BASE_PPI - verticalSafetyPx + BOUNDS_EPSILON_PX
    ) {
      outsideSafety = true
    }
    if (
      bounds.left < -BOUNDS_EPSILON_PX ||
      bounds.top  < -BOUNDS_EPSILON_PX ||
      bounds.left + bounds.width  > sheetSize.width_in  * BASE_PPI + BOUNDS_EPSILON_PX ||
      bounds.width > maximumArtworkWidthPx + BOUNDS_EPSILON_PX ||
      bounds.top  + bounds.height > sheetSize.height_in * BASE_PPI + BOUNDS_EPSILON_PX
    ) {
      outsideSheet = true
    }
  })
  return { outsideSafety, outsideSheet }
}

// Returns true if a canvas has any user-placed objects (non-safety-margin)
function canvasHasContent(canvas) {
  if (!canvas) return false
  return canvas.getObjects().some(o => o.name !== SAFETY_MARGIN_OBJECT_NAME)
}

function isSelectableCanvasObject(obj) {
  if (!obj) return false
  if (obj.name === SAFETY_MARGIN_OBJECT_NAME) return false
  if (obj.type === 'activeSelection') return false
  return true
}

function isPlacedArtworkObject(obj) {
  return isSelectableCanvasObject(obj) && obj.uploadedImageId != null
}

function adjacentCopyPosition(sourceObject, clone, canvasObjects, sheetSize) {
  const sourceBounds = getObjectBounds(sourceObject)
  const cloneBounds = getObjectBounds(clone)
  const horizontalSafetyPx = Number(
    sheetSize.horizontal_safety_margin_in ?? SAFETY_MARGIN_IN
  ) * BASE_PPI
  const verticalSafetyPx = SAFETY_MARGIN_IN * BASE_PPI
  const safeArea = {
    left: horizontalSafetyPx,
    top: verticalSafetyPx,
    right: sheetSize.width_in * BASE_PPI - horizontalSafetyPx,
    bottom: sheetSize.height_in * BASE_PPI - verticalSafetyPx,
  }
  const obstacleBounds = (canvasObjects || [])
    .filter(isPlacedArtworkObject)
    .map(getObjectBounds)
  const available = findCopyBounds(
    sourceBounds, cloneBounds, obstacleBounds, safeArea,
    COPY_PLACEMENT_GAP_PX, BOUNDS_EPSILON_PX
  )
  if (!available) return null

  return {
    left: (Number(clone.left) || 0) + available.left - cloneBounds.left,
    top: (Number(clone.top) || 0) + available.top - cloneBounds.top,
  }
}

function selectedArtworkObjects(selection) {
  if (selection?.type === 'activeSelection') {
    return (selection.getObjects?.() || []).filter(isPlacedArtworkObject)
  }

  return isPlacedArtworkObject(selection) ? [selection] : []
}

function formatInches(value) {
  return Number(value).toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1')
}

function formatDollars(cents) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format((cents || 0) / 100)
}

function DynamicPriceSummary({ quote }) {
  if (!quote?.hasContent) {
    return (
      <div className="editor-dynamic-price editor-dynamic-price--empty" data-testid="dynamic-price-summary">
        Add artwork to calculate price
      </div>
    )
  }

  return (
    <div className="editor-dynamic-price" data-testid="dynamic-price-summary">
      <span>Estimated total</span>
      <strong data-testid="dynamic-price-total">{formatDollars(quote.totalCents)}</strong>
    </div>
  )
}

const PRICING_QUOTE_FIELDS = [
  'hasContent',
  'widthInches',
  'heightInches',
  'totalHeightInches',
  'sheetCount',
  'squareFeet',
  'rateCents',
  'totalCents',
  'exceedsMaximum',
]

function pricingQuotesEqual(left, right) {
  if (left === right) return true
  if (!left || !right) return false

  return PRICING_QUOTE_FIELDS.every(field => Object.is(left[field], right[field])) &&
    JSON.stringify(left.sheetHeightsInches || []) === JSON.stringify(right.sheetHeightsInches || [])
}

function SaveStatusIndicator({ status, lastAutosavedAt }) {
  if (!status || status === 'idle') return null

  let label = 'Saving…'
  if (status === 'saved') {
    const savedAt = lastAutosavedAt ? new Date(lastAutosavedAt) : null
    const recent = savedAt && (Date.now() - savedAt.getTime()) < 60_000
    label = recent
      ? 'Autosaved just now'
      : `Autosaved ${savedAt?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) || ''}`.trim()
  } else if (status === 'offline') {
    label = 'Offline — changes saved on this device'
  } else if (status === 'reconnected') {
    label = 'Reconnected — saving…'
  } else if (status === 'retrying' || status === 'failed') {
    label = 'Save failed — retrying'
  } else if (status === 'conflict') {
    label = 'Save conflict — choose a version to resume saving'
  }

  return <span className={`editor-save-status editor-save-status--${status}`} role="status">{label}</span>
}

export default function CanvasEditor({
  images,
  arrangementImages,
  transitioning = false,
  sheetSize,
  vendorToken,
  builderSessionId,
  customerPortalPath,
  sheetSizes,
  selectedSheetSizeId,
  onSelectedSheetSizeIdChange,
  imageConfigs = {},
  onImagesChange,
  onImageConfigsChange,
  uploads,
  onContinue,
  onBack,
  onAddArtwork,
  initialLayout,
  loadError,
  pricingSettings,
  onPricingChange,
  builderDraftPublicId,
  onLayoutChange,
  saveStatus,
  lastAutosavedAt,
  onStartNew,
  guidedBuildRequestId,
  onGuidedBuildError,
  reviewPreviewRequestId,
  onReviewPreviewCaptured,
  onReviewPreviewError,
  onDeleteArtworkSource,
  onBackgroundPreview,
  onBackgroundApply,
  vendorArtworks,
  vendorArtworksLoading,
  vendorArtworksError,
  shopDesignsSearch,
  onImportVendorArtwork,
  maxImageCount,
}) {
  const {
    uploadFiles,
    uploadingFiles = [],
    uploadErrors = [],
  } = uploads || {}
  const [zoom, setZoom] = useState(1.0)
  const [showGrid, setShowGrid] = useState(true)
  const compactEditor = useCompactEditor()
  const [mobileEditing, setMobileEditing] = useState(false)
  const navigationOnly = compactEditor && !mobileEditing
  const [imagePool]    = useState(() => new Map())
  const [imageUrls, setImageUrls]   = useState({})  // uploadedImageId -> presigned URL
  const { getAppUrl, keepUrl, releaseUrl } = useAppArtworkUrls(images, vendorToken, builderSessionId, builderDraftPublicId)
  const [outsideSafety, setOutsideSafety] = useState(false)
  const [outsideSheet,  setOutsideSheet]  = useState(false)
  const [dropError,     setDropError]     = useState(null)
  const [canvasTransformError, setCanvasTransformError] = useState(null)
  const [uploadDragOver, setUploadDragOver] = useState(false)
  const [unplaceableItems, setUnplaceableItems] = useState([])
  const [dragOver, setDragOver] = useState(false)
  const [canvasRevision, setCanvasRevision] = useState(0)
  const [isAutoArranging, setIsAutoArranging] = useState(false)
  const [isStartingNew, setIsStartingNew] = useState(false)
  const [startNewError, setStartNewError] = useState(null)
  const [deleteArtworkError, setDeleteArtworkError] = useState(null)
  const [deletingImageIds, setDeletingImageIds] = useState(new Set())
  const [selectedCanvasObject, setSelectedCanvasObject] = useState(null)
  const [initialRestoreStatus, setInitialRestoreStatus] = useState(RESTORE_NOT_STARTED)
  const [guidedCaptureRequestId, setGuidedCaptureRequestId] = useState(null)
  const [buildProgress, setBuildProgress] = useState('Loading artwork…')
  const isGuidedBuilding = guidedBuildRequestId !== undefined && guidedBuildRequestId !== null
  const isReviewPreviewCapturing = reviewPreviewRequestId !== undefined && reviewPreviewRequestId !== null
  const isHiddenCanvasMode = isGuidedBuilding || isReviewPreviewCapturing
  const fileInputRef = useRef(null)
  const lastEmittedPricingQuoteRef = useRef()
  const isProgrammaticCanvasResizeRef = useRef(false)
  const isProgrammaticLayoutRestoreRef = useRef(false)
  const isAtomicLayoutMutationRef = useRef(false)
  const isAutoArrangingRef = useRef(false)
  const autoArrangeGenerationRef = useRef(0)
  const handledGuidedBuildRequestRef = useRef(null)
  const completedGuidedBuildRequestRef = useRef(null)
  const handledReviewPreviewRequestRef = useRef(null)
  const reviewPreviewCaptureGenerationRef = useRef(0)
  // `initialLayout` is a mount-time restore snapshot. Builder may later pass its
  // live layout back through this prop, but that state must never be restored again.
  const initialLayoutRef = useRef(initialLayout)
  const initialRestoreStatusRef = useRef(RESTORE_NOT_STARTED)
  const isStartingNewRef = useRef(false)
  const deletingImageIdsRef = useRef(new Set())
  const imageLoadGenerationRef = useRef(0)
  const skipNextLayoutEmissionRef = useRef(false)
  const forceNextLayoutEmissionRef = useRef(false)
  const lastEmittedLayoutRef = useRef(null)
  const lastReportedSheetSizeIdRef = useRef(sheetSize.id)
  const imageConfigsRef = useRef(imageConfigs)
  const historyGenerationRef = useRef(0)
  const historyMutationCompleteRef = useRef(null)
  imageConfigsRef.current = imageConfigs

  const updateImageConfigs = useCallback(updater => {
    const previousConfigs = imageConfigsRef.current || {}
    const nextConfigs = typeof updater === 'function'
      ? updater(previousConfigs)
      : updater

    imageConfigsRef.current = nextConfigs
    onImageConfigsChange?.(nextConfigs)
  }, [onImageConfigsChange])

  const updateImageConfig = useCallback((id, updater) => {
    updateImageConfigs(previousConfigs => ({
      ...previousConfigs,
      [id]: updater(previousConfigs[id]),
    }))
  }, [updateImageConfigs])

  const updateInitialRestoreStatus = useCallback(status => {
    initialRestoreStatusRef.current = status
    setInitialRestoreStatus(status)
  }, [])

  useEffect(() => () => {
    autoArrangeGenerationRef.current += 1
    historyGenerationRef.current += 1
    isAutoArrangingRef.current = false
  }, [])

  // Include the background revision in the readiness key. This prevents a
  // completed removal from briefly reusing the old, already-loaded original
  // while the new Active Storage URL is still being hydrated into Fabric.
  const imageLoadKey = useCallback(image => (
    `${String(image.id)}:${Number(image.backgroundRevision ?? 0)}`
  ), [])

  // loadedImageKeys tracks which source revisions have been loaded into imagePool.
  // imagePool is a Map (not React state), so mutations don't trigger re-renders.
  // This state is the signal that tells the restore effect when images are ready.
  const [loadedImageKeys, setLoadedImageKeys] = useState(new Set())

  const [sheets, setSheets] = useState(() => initialSheetDefinitions(initialLayout))
  const [activeSheetIndex, setActiveSheetIndex] = useState(0)
  const [sheetAnnouncement, setSheetAnnouncement] = useState('')
  const canvasRefs        = useRef({})  // Map<sheetId, Canvas>
  const canvasCleanupRefs = useRef({})  // Map<sheetId, () => void> — cleanup fn per canvas
  const canvasReadyCache = useRef({})

  const canvasContainerRef = useRef(null)
  const canvasWrapperRef   = useRef(null)
  const sheetNavigationRef = useRef(null)
  const canUpload = typeof uploadFiles === 'function'
  const sortedSheetSizes = useMemo(() => (
    [...(sheetSizes || [])].sort((a, b) => (a.width_in * a.height_in) - (b.width_in * b.height_in))
  ), [sheetSizes])

  // Derived: active canvas from canvasRefs
  const sheetsRef = useRef(sheets)
  sheetsRef.current = sheets
  const activeSheetIndexRef = useRef(activeSheetIndex)
  activeSheetIndexRef.current = activeSheetIndex

  // A successful consolidation accept intentionally collapses to one sheet
  // and wants its own announcement to survive; it flags this ref (see the
  // accept effect below) so this cleanup skips exactly that one transition.
  const suppressSheetCountAnnouncementClearRef = useRef(false)
  useEffect(() => {
    if (sheets.length > 1) return
    if (suppressSheetCountAnnouncementClearRef.current) {
      suppressSheetCountAnnouncementClearRef.current = false
      return
    }
    setSheetAnnouncement('')
  }, [sheets.length])

  const getActiveCanvas = useCallback(() => {
    const activeSheet = sheetsRef.current[activeSheetIndexRef.current]
    return activeSheet ? canvasRefs.current[activeSheet.id] : null
  }, [])

  const handleSelectSheet = useCallback(index => {
    const nextSheet = sheetsRef.current[index]
    if (!nextSheet) return

    setActiveSheetIndex(index)
    setSheetAnnouncement(`Viewing Sheet ${index + 1} of ${sheetsRef.current.length}.`)
    const activeObject = canvasRefs.current[nextSheet.id]?.getActiveObject?.()
    setSelectedCanvasObject(isSelectableCanvasObject(activeObject) ? activeObject : null)
  }, [])

  const getPlacedObjects = useCallback(() => (
    sheetsRef.current.flatMap(sheet => (
      canvasRefs.current[sheet.id]?.getObjects()
        .filter(isPlacedArtworkObject) || []
    ))
  ), [])

  const isDynamicPricing = pricingSettings?.pricing_mode === 'per_square_foot'
  const nominalSheetWidth = isDynamicPricing ? pricingSettings.width_inches : sheetSize.width_in
  const activeSheetWidthPolicy = useMemo(
    () => sheetWidthPolicy(nominalSheetWidth),
    [nominalSheetWidth]
  )
  const pricingLayout = useMemo(() => filterLayoutToIncludedSources(
    serializeLayout(
      sheets,
      {
        id: sheetSize.id,
        width_in: activeSheetWidthPolicy.layoutWidthIn,
        height_in: isDynamicPricing ? pricingSettings.max_height_inches : sheetSize.height_in,
      },
      canvasRefs.current
    ),
    images
  ), [
    sheets,
    sheetSize,
    isDynamicPricing,
    pricingSettings,
    activeSheetWidthPolicy.layoutWidthIn,
    canvasRevision,
    images,
  ])
  const dynamicQuote = useMemo(
    () => calculateDynamicPricing(pricingLayout, pricingSettings),
    [pricingLayout, pricingSettings]
  )
  const layoutSheets = useMemo(() => sheets.map((sheet, index) => ({
    ...sheet,
    width_in: activeSheetWidthPolicy.layoutWidthIn,
    height_in: isDynamicPricing
      ? dynamicQuote?.sheetHeightsInches?.[index] || sheet.height_in || pricingSettings.min_height_inches
      : sheetSize.height_in,
  })), [
    dynamicQuote?.sheetHeightsInches,
    isDynamicPricing,
    pricingSettings?.min_height_inches,
    activeSheetWidthPolicy.layoutWidthIn,
    sheetSize.height_in,
    sheetSize.width_in,
    sheets,
  ])
  const activeLayoutSheet = layoutSheets[activeSheetIndex] || layoutSheets[0]
  const effectiveSheetSize = useMemo(() => {
    return {
      ...sheetSize,
      ...(isDynamicPricing && { label: 'Automatic-height sheet' }),
      width_in: activeSheetWidthPolicy.layoutWidthIn,
      nominal_width_in: activeSheetWidthPolicy.nominalWidthIn,
      hard_width_limit_in: activeSheetWidthPolicy.hardWidthLimitIn,
      horizontal_safety_margin_in: activeSheetWidthPolicy.horizontalSafetyMarginIn,
      height_in: isDynamicPricing
        ? activeLayoutSheet?.height_in || pricingSettings.min_height_inches
        : sheetSize.height_in,
    }
  }, [
    activeLayoutSheet?.height_in,
    activeSheetWidthPolicy,
    isDynamicPricing,
    pricingSettings?.min_height_inches,
    sheetSize,
  ])
  const effectiveSheetSizeRef = useRef(effectiveSheetSize)
  effectiveSheetSizeRef.current = effectiveSheetSize
  const sheetSizeForIndex = useCallback(index => {
    return {
      ...sheetSize,
      ...(isDynamicPricing && { label: `Automatic-height sheet ${index + 1}` }),
      width_in: activeSheetWidthPolicy.layoutWidthIn,
      nominal_width_in: activeSheetWidthPolicy.nominalWidthIn,
      hard_width_limit_in: activeSheetWidthPolicy.hardWidthLimitIn,
      horizontal_safety_margin_in: activeSheetWidthPolicy.horizontalSafetyMarginIn,
      height_in: isDynamicPricing
        ? layoutSheets[index]?.height_in || pricingSettings.min_height_inches
        : sheetSize.height_in,
    }
  }, [
    activeSheetWidthPolicy,
    isDynamicPricing,
    layoutSheets,
    pricingSettings?.min_height_inches,
    sheetSize,
  ])
  const autofillSheetSize = useMemo(() => (
    isDynamicPricing
      ? {
          ...effectiveSheetSize,
          height_in: pricingSettings.max_height_inches,
        }
      : effectiveSheetSize
  ), [effectiveSheetSize, isDynamicPricing, pricingSettings?.max_height_inches])
  const artworkConstraintSheetSize = useMemo(() => (
    isDynamicPricing
      ? {
          ...autofillSheetSize,
          width_in: activeSheetWidthPolicy.nominalWidthIn,
          height_in: pricingSettings.max_height_inches,
        }
      : sheetSize
  ), [
    activeSheetWidthPolicy.nominalWidthIn,
    autofillSheetSize,
    isDynamicPricing,
    pricingSettings?.max_height_inches,
    sheetSize,
  ])

  useEffect(() => {
    if (!onPricingChange) return
    if (pricingQuotesEqual(lastEmittedPricingQuoteRef.current, dynamicQuote)) return

    lastEmittedPricingQuoteRef.current = dynamicQuote
    onPricingChange(dynamicQuote)
  }, [dynamicQuote, onPricingChange])

  // --- Undo/redo ---
  const getState = useCallback(() => {
    return {
      sheets: sheetsRef.current.map(sheet => ({
        sheetId: sheet.id,
        items: canvasRefs.current[sheet.id]
          ? getCanvasState(canvasRefs.current[sheet.id])
          : [],
      })),
      imageConfigs: Object.fromEntries(
        Object.entries(imageConfigsRef.current || {}).map(([id, config]) => [id, { ...config }])
      ),
    }
  }, [])

  const applyState = useCallback((state, onComplete) => {
    const operationGeneration = historyGenerationRef.current
    const shouldApply = () => historyGenerationRef.current === operationGeneration
    const legacyCanvasOnlyState = Array.isArray(state)
    const sheetStates = legacyCanvasOnlyState ? state : (state?.sheets || [])
    if (!legacyCanvasOnlyState) updateImageConfigs(state?.imageConfigs || {})

    // Guard: if no sheets, onComplete must still fire so isRestoringRef is lowered
    if (sheetStates.length === 0) { onComplete?.(); return }
    const targetSheets = legacyCanvasOnlyState
      ? sheetsRef.current
      : sheetStates.map(({ sheetId }, index) => ({
          id: sheetId,
          label: `Sheet ${index + 1}`,
        }))

    const applyToCanvases = () => {
      if (!shouldApply()) { onComplete?.(); return }

      isAtomicLayoutMutationRef.current = true
      let pending = sheetStates.length
      const done = () => {
        if (--pending !== 0) return
        isAtomicLayoutMutationRef.current = false
        historyMutationCompleteRef.current?.()
        onComplete?.()
      }
      sheetStates.forEach(({ sheetId, items }) => {
        const canvas = canvasRefs.current[sheetId]
        try {
          if (canvas) applyCanvasState(canvas, items, imagePool, done, shouldApply)
          else done()
        } catch {
          done()
        }
      })
    }

    const currentIds = sheetsRef.current.map(sheet => sheet.id)
    const targetIds = targetSheets.map(sheet => sheet.id)
    if (JSON.stringify(currentIds) === JSON.stringify(targetIds)) {
      applyToCanvases()
      return
    }

    const targetIdSet = new Set(targetIds)
    sheetsRef.current
      .filter(sheet => !targetIdSet.has(sheet.id))
      .forEach(sheet => {
        canvasCleanupRefs.current[sheet.id]?.()
        delete canvasCleanupRefs.current[sheet.id]
        delete canvasReadyCache.current[sheet.id]
        delete canvasRefs.current[sheet.id]
      })
    sheetsRef.current = targetSheets
    setSheets(targetSheets)
    setActiveSheetIndex(index => Math.min(index, targetSheets.length - 1))

    let attempts = 0
    const waitForCanvases = () => {
      if (!shouldApply()) { onComplete?.(); return }
      if (targetSheets.every(sheet => canvasRefs.current[sheet.id])) {
        applyToCanvases()
        return
      }
      attempts += 1
      if (attempts >= 120) { onComplete?.(); return }
      if (typeof window.requestAnimationFrame === 'function') window.requestAnimationFrame(waitForCanvases)
      else window.setTimeout(waitForCanvases, 0)
    }
    waitForCanvases()
  }, [imagePool, updateImageConfigs])

  const { snapshot, undo, redo, reset: resetHistory, canUndo, canRedo, startAtomicBatch } = useUndoRedo(getState, applyState)

  const canvasWidthPx  = effectiveSheetSize.width_in  * BASE_PPI
  const canvasHeightPx = effectiveSheetSize.height_in * BASE_PPI
  const canvasSizeRef = useRef({ width: canvasWidthPx, height: canvasHeightPx })
  canvasSizeRef.current = { width: canvasWidthPx, height: canvasHeightPx }

  const updateZoom = useCallback((nextZoom) => {
    if (!Number.isFinite(nextZoom) || nextZoom <= 0) return

    setZoom(previousZoom => (
      Math.abs(previousZoom - nextZoom) < ZOOM_EPSILON ? previousZoom : nextZoom
    ))
  }, [])

  // --- Fit zoom ---
  const computeFitZoom = useCallback(() => {
    if (!canvasContainerRef.current) return null
    const { width: availableWidth, height: availableHeight } =
      canvasContainerRef.current.getBoundingClientRect()
    if (availableWidth <= 0 || availableHeight <= 0) return null

    // Navigation sits above the rulers and can wrap onto several rows.
    const navigation = sheetNavigationRef.current
    const navigationHeight = navigation
      ? navigation.getBoundingClientRect().height +
        (parseFloat(getComputedStyle(navigation).marginBottom) || 0)
      : 0

    const { width: currentCanvasWidth, height: currentCanvasHeight } = canvasSizeRef.current
    return calculateFitZoom({
      availableWidth,
      availableHeight: availableHeight - navigationHeight,
      canvasWidth: currentCanvasWidth,
      canvasHeight: currentCanvasHeight,
    })
  }, [])

  // Start at 100% even for long sheets. Fitting the whole sheet is an explicit
  // choice; restoring a draft or resizing the workspace must not shrink it.
  const handleFit = useCallback(() => {
    const fitZoom = computeFitZoom()
    if (fitZoom === null) return
    updateZoom(fitZoom)
  }, [computeFitZoom, updateZoom])

  // --- Load presigned URLs + populate imagePool ---
  useEffect(() => {
    if (!images || images.length === 0) return
    const generation = ++imageLoadGenerationRef.current
    let cancelled = false
    const controller = new AbortController()
    let loadedCount = 0
    const isStale = () => cancelled || imageLoadGenerationRef.current !== generation
    images.forEach(image => {
      const loadKey = imageLoadKey(image)
      const recoveredUrl = image.imageUrl || image.previewUrl
      loadArtworkImage({
        signal: controller.signal,
        resolveUrl: (attempt, signal) => attempt > 0
          ? getAppUrl(image, signal)
          : recoveredUrl || fetchImageUrl(vendorToken, builderSessionId, image.id, builderDraftPublicId, signal),
        loadImage: (url, signal) => FabricImage.fromURL(url, { crossOrigin: 'anonymous', signal }),
        releaseUrl,
        onAttempt: attempt => {
          if (isStale() || !isGuidedBuilding) return
          setBuildProgress(attempt > 1
            ? `Retrying ${image.originalFilename || 'artwork'} (attempt ${attempt} of 4)…`
            : `Loading artwork: ${loadedCount} of ${images.length} designs ready…`)
        },
      })
        .then(({ image: img, url }) => {
          if (isStale()) { releaseUrl(url); return }
          setImageUrls(prev => ({ ...prev, [image.id]: url }))
          imagePool.set(String(image.id), img)
          refreshPlacedImageElements(Object.values(canvasRefs.current), image.id, img)
          keepUrl(image.id, url)
          setLoadedImageKeys(prev => new Set([...prev, loadKey]))
          loadedCount += 1
          if (isGuidedBuilding) setBuildProgress(`Loading artwork: ${loadedCount} of ${images.length} designs ready…`)
        })
        .catch(() => {
          if (isStale()) return
          if (isHiddenCanvasMode) {
            cancelled = true
            controller.abort()
            const sourceName = image.originalFilename || 'Artwork'
            if (isGuidedBuilding) {
              onGuidedBuildError?.(`Could not load ${sourceName} after 4 attempts. Your artwork and settings are still here. Retry build, or remove and replace this design if it keeps failing.`)
            } else {
              onReviewPreviewError?.(`Could not load ${sourceName} to prepare the design preview. Return to the layout and try again.`)
            }
          }
        })
    })
    return () => {
      cancelled = true
      controller.abort()
    }
  }, [
    images,
    vendorToken,
    builderSessionId,
    builderDraftPublicId,
    guidedBuildRequestId,
    reviewPreviewRequestId,
    imageLoadKey,
    imagePool,
    getAppUrl,
    keepUrl,
    releaseUrl,
    isGuidedBuilding,
    isHiddenCanvasMode,
    onGuidedBuildError,
    onReviewPreviewError,
  ])

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const handleUploadInputChange = useCallback((e) => {
    uploadFiles?.(e.target.files)
    e.target.value = ''
  }, [uploadFiles])

  const handleUploadDrop = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setUploadDragOver(false)
    if (e.dataTransfer?.files?.length) {
      uploadFiles?.(e.dataTransfer.files)
    }
  }, [uploadFiles])

  const handleUploadDragEnter = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setUploadDragOver(true)
  }, [])

  const handleUploadDragOver = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy'
    setUploadDragOver(true)
  }, [])

  const handleUploadDragLeave = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setUploadDragOver(false)
  }, [])

  const handleImageConfigChange = useCallback((id, config) => {
    const sourceImage = images?.find(image => String(image.id) === String(id))
    updateImageConfig(id, previousConfig => (
      normalizeArtworkConfig({ ...(previousConfig || {}), ...config }, sourceImage)
    ))
  }, [images, updateImageConfig])

  const handleRetryImage = useCallback((image) => {
    onImagesChange?.(prev => prev.filter(img => img.id !== image.id))
    openFilePicker()
  }, [onImagesChange, openFilePicker])

  const handleSelectionChange = useCallback((obj) => {
    setSelectedCanvasObject(isSelectableCanvasObject(obj) ? obj : null)
  }, [])

  const handleSelectCanvasObject = useCallback((obj) => {
    setSelectedCanvasObject(isSelectableCanvasObject(obj) ? obj : null)
  }, [])

  // --- Bounds check helper ---
  const runBoundsCheck = useCallback(() => {
    const canvas = getActiveCanvas()
    if (!canvas) return
    const { outsideSafety, outsideSheet } = checkBounds(canvas, effectiveSheetSizeRef.current)
    setOutsideSafety(outsideSafety)
    setOutsideSheet(outsideSheet)
  }, [getActiveCanvas])

  const handleCanvasMutation = useCallback(() => {
    if (
      isProgrammaticCanvasResizeRef.current ||
      isProgrammaticLayoutRestoreRef.current ||
      isAtomicLayoutMutationRef.current
    ) return

    runBoundsCheck()
    setCanvasRevision(revision => revision + 1)
  }, [runBoundsCheck])
  historyMutationCompleteRef.current = () => {
    forceNextLayoutEmissionRef.current = true
    handleCanvasMutation()
  }

  // Run only after explicit deletion, never during hydration or layout rebuilding.
  // Update refs before capturing history so Undo restores the page and its artwork.
  const pruneEmptySheetsAfterDeletion = useCallback(() => {
    const currentSheets = sheetsRef.current
    const retained = currentSheets.filter(sheet => {
      const canvas = canvasRefs.current[sheet.id]
      return !canvas || canvasHasContent(canvas)
    })
    if (retained.length === 0) retained.push(currentSheets[0])
    if (retained.length === currentSheets.length) {
      if (currentSheets.length === 1 && !canvasHasContent(canvasRefs.current[currentSheets[0].id])) {
        setSheetAnnouncement('')
      }
      return
    }

    const activeId = currentSheets[activeSheetIndexRef.current]?.id
    const retainedIds = new Set(retained.map(sheet => sheet.id))
    currentSheets.forEach(sheet => {
      if (retainedIds.has(sheet.id)) return
      canvasCleanupRefs.current[sheet.id]?.()
      delete canvasCleanupRefs.current[sheet.id]
      delete canvasReadyCache.current[sheet.id]
      delete canvasRefs.current[sheet.id]
    })
    const nextSheets = retained.map((sheet, index) => ({ ...sheet, label: `Sheet ${index + 1}` }))
    const retainedActiveIndex = nextSheets.findIndex(sheet => sheet.id === activeId)
    const nextIndex = retainedActiveIndex >= 0
      ? retainedActiveIndex
      : Math.min(activeSheetIndexRef.current, nextSheets.length - 1)
    sheetsRef.current = nextSheets
    activeSheetIndexRef.current = nextIndex
    setSheets(nextSheets)
    setActiveSheetIndex(nextIndex)
    setSelectedCanvasObject(null)
    const removedCount = currentSheets.length - nextSheets.length
    suppressSheetCountAnnouncementClearRef.current = nextSheets.length === 1
    setSheetAnnouncement(`Removed ${removedCount} empty sheet${removedCount === 1 ? '' : 's'}. Viewing Sheet ${nextIndex + 1} of ${nextSheets.length}.`)
  }, [])

  const handleRemoveImage = useCallback(async (id) => {
    const imageId = String(id)
    if (deletingImageIdsRef.current.has(imageId)) return

    deletingImageIdsRef.current.add(imageId)
    setDeletingImageIds(new Set(deletingImageIdsRef.current))
    setDeleteArtworkError(null)

    try {
      try {
        if (onDeleteArtworkSource) {
          await onDeleteArtworkSource(id)
        } else if (builderDraftPublicId) {
          await deleteUpload(vendorToken, builderSessionId, id, builderDraftPublicId)
        } else {
          await deleteUpload(vendorToken, builderSessionId, id)
        }
      } catch (error) {
        const detail = error instanceof Error && error.message
          ? ` ${error.message}`
          : ' Please try again.'
        setDeleteArtworkError(`Could not delete artwork.${detail}`)
        return
      }

      // Invalidate pending URL/Fabric hydration before removing the image state.
      // The next images effect gets a fresh generation, so future uploads remain loadable.
      imageLoadGenerationRef.current += 1
      historyGenerationRef.current += 1

      isAtomicLayoutMutationRef.current = true
      try {
        Object.values(canvasRefs.current).forEach(canvas => {
          if (!canvas || typeof canvas.getObjects !== 'function') return

          if (selectionReferencesUploadedImage(canvas.getActiveObject?.(), imageId)) {
            canvas.discardActiveObject?.()
          }

          const matchingObjects = canvas.getObjects().filter(object => (
            isSelectableCanvasObject(object) && String(object.uploadedImageId) === imageId
          ))
          matchingObjects.forEach(object => canvas.remove(object))
          if (matchingObjects.length > 0) canvas.requestRenderAll?.()
        })
      } finally {
        isAtomicLayoutMutationRef.current = false
      }

      if (String(selectedCanvasObject?.uploadedImageId) === imageId) {
        setSelectedCanvasObject(null)
      }
      if (!onDeleteArtworkSource) {
        onImagesChange?.(previousImages => (
          previousImages.filter(image => String(image.id) !== imageId)
        ))
        updateImageConfigs(previousConfigs => {
          const nextConfigs = { ...previousConfigs }
          delete nextConfigs[id]
          return nextConfigs
        })
      }
      setImageUrls(previousUrls => {
        const nextUrls = { ...previousUrls }
        delete nextUrls[id]
        return nextUrls
      })
      setLoadedImageKeys(previousKeys => {
        return new Set([...previousKeys].filter(key => !key.startsWith(`${imageId}:`)))
      })
      imagePool.delete(imageId)
      pruneEmptySheetsAfterDeletion()
      resetHistory()

      // Fabric removal events were suppressed by the atomic guard above.
      forceNextLayoutEmissionRef.current = true
      handleCanvasMutation()
    } finally {
      deletingImageIdsRef.current.delete(imageId)
      setDeletingImageIds(new Set(deletingImageIdsRef.current))
    }
  }, [
    builderDraftPublicId,
    builderSessionId,
    handleCanvasMutation,
    imagePool,
    onDeleteArtworkSource,
    onImagesChange,
    pruneEmptySheetsAfterDeletion,
    resetHistory,
    selectedCanvasObject,
    updateImageConfigs,
    vendorToken,
  ])

  const handleArtworkInstanceConfigChange = useCallback((objectId, config) => {
    const canvas = getActiveCanvas()
    const target = canvas?.getObjects()
      .find(obj => isSelectableCanvasObject(obj) && obj.objectId === objectId)
    if (!target) return

    const printWidthIn = Number(config.printWidthIn)
    const printHeightIn = Number(config.printHeightIn)
    if (printWidthIn > 0 && printHeightIn > 0) {
      canvas.fire?.('before:transform', { transform: { target } })
      target.set?.({
        scaleX: (printWidthIn * BASE_PPI) / target.width,
        scaleY: (printHeightIn * BASE_PPI) / target.height,
      })
    }
    // One Fabric object is one physical copy. Desired totals live in imageConfigs.
    target.set?.({ quantity: 1 })
    target.setCoords?.()
    setSelectedCanvasObject(activateCanvasObject(canvas, target) ? target : null)
    canvas?.requestRenderAll?.()
    if (typeof canvas?.fire === 'function') {
      canvas.fire('object:modified', { target })
    } else {
      handleCanvasMutation()
    }
  }, [getActiveCanvas, handleCanvasMutation])

  // --- Canvas ready callback from SheetCanvas — curried by sheetId ---
  // Memoized per sheetId so SheetCanvas receives a stable function reference.
  const getCanvasReadyHandler = useCallback((sheetId) => {
    if (!canvasReadyCache.current[sheetId]) {
      canvasReadyCache.current[sheetId] = (canvas) => handleCanvasReadyInner(sheetId, canvas)
    }
    return canvasReadyCache.current[sheetId]
  }, [])
  const handleCanvasReadyInner = useCallback((sheetId, canvas) => {
    canvasRefs.current[sheetId] = canvas
    const keepSafetyMarginOnTop = () => bringSafetyMarginToFront(canvas)
    canvas.on('object:modified', handleCanvasMutation)
    canvas.on('object:added',    handleCanvasMutation)
    canvas.on('object:removed',  handleCanvasMutation)  // re-evaluate Continue button when last image deleted
    canvas.on('object:added',    keepSafetyMarginOnTop)
    canvas.on('object:modified', keepSafetyMarginOnTop)
    // Snapshot on every canvas mutation (debounced)
    let debounceTimer = null
    const debouncedSnapshot = () => {
      if (isProgrammaticCanvasResizeRef.current) return
      if (isProgrammaticLayoutRestoreRef.current) return
      if (isAtomicLayoutMutationRef.current) return

      clearTimeout(debounceTimer)
      debounceTimer = setTimeout(() => {
        if (
          !isProgrammaticCanvasResizeRef.current &&
          !isProgrammaticLayoutRestoreRef.current &&
          !isAtomicLayoutMutationRef.current
        ) snapshot()
      }, 200)
    }
    canvas.on('object:modified', debouncedSnapshot)
    canvas.on('object:added',    debouncedSnapshot)
    canvas.on('object:removed',  debouncedSnapshot)
    // Store cleanup so sheet removal can clear listeners + pending timer
    canvasCleanupRefs.current[sheetId] = () => {
      clearTimeout(debounceTimer)
      canvas.off('object:modified', handleCanvasMutation)
      canvas.off('object:added',    handleCanvasMutation)
      canvas.off('object:removed',  handleCanvasMutation)
      canvas.off('object:added',    keepSafetyMarginOnTop)
      canvas.off('object:modified', keepSafetyMarginOnTop)
      canvas.off('object:modified', debouncedSnapshot)
      canvas.off('object:added',    debouncedSnapshot)
      canvas.off('object:removed',  debouncedSnapshot)
    }
  }, [handleCanvasMutation, snapshot])

  // Re-run bounds check when active sheet changes
  useEffect(() => {
    runBoundsCheck()
  }, [
    activeSheetIndex,
    effectiveSheetSize.height_in,
    effectiveSheetSize.width_in,
    runBoundsCheck,
  ])

  // --- Restore effect (edit back-link) ---
  // Fires after all referenced images are loaded into imagePool.
  // MUST use placeImage (not applyCanvasState) — layout_json uses inch coords,
  // applyCanvasState expects undo-snapshot format with pixel coords and scale factors.
  const initialRestoreImageIds = (initialLayoutRef.current?.sheets || [])
    .flatMap(sheet => (sheet.items || []).map(item => item.uploaded_image_id))
    .filter(id => id !== undefined && id !== null)
    .map(String)
  const initialRestoreImagesLoaded = initialRestoreImageIds.every(id => {
    const image = images?.find(candidate => String(candidate.id) === id)
    return image ? loadedImageKeys.has(imageLoadKey(image)) : false
  })

  useEffect(() => {
    if (initialRestoreStatusRef.current !== RESTORE_NOT_STARTED) return

    const layoutToRestore = initialLayoutRef.current
    if (sheets.length === 0) return

    const sheetsToRestore = layoutToRestore?.sheets || []
    const itemsToRestore = sheetsToRestore.flatMap((sheetData, index) =>
      (sheetData.items || []).map(item => ({ item, index }))
    )
    if (itemsToRestore.length === 0) {
      updateInitialRestoreStatus(RESTORE_COMPLETE)
      return
    }

    if (!initialRestoreImagesLoaded) return

    // SheetCanvas registers its Fabric instance before this parent effect runs.
    const allCanvasesReady = sheetsToRestore.every((_, i) =>
      !!canvasRefs.current[sheets[i]?.id]
    )
    if (!allCanvasesReady) return

    updateInitialRestoreStatus(RESTORE_RESTORING)
    isProgrammaticLayoutRestoreRef.current = true

    let cancelled = false
    let restoreCompleted = false
    const restoredObjects = []

    // Place items on their persisted physical sheets.
    // layout_json items use inch coordinates (x_in, y_in, width_in, height_in).
    // placeImage takes pixel coords (x_in * BASE_PPI, y_in * BASE_PPI) + inch dims.
    const placements = itemsToRestore.map(({ item, index }) => {
      const canvas = canvasRefs.current[sheets[index]?.id]
      return placeImage(
        imagePool,
        item.uploaded_image_id,
        canvas,
        item.x_in * BASE_PPI,
        item.y_in * BASE_PPI,
        item.width_in,
        item.height_in,
        null,
        {
          angle: item.rotation_deg,
          flipX: item.flip_x,
          flipY: item.flip_y,
          objectId: item.instance_id,
          // Legacy layout items may contain aggregate quantity metadata. One
          // restored Fabric object still represents exactly one physical copy.
          quantity: 1,
          shouldPlace: () => !cancelled,
        }
      ).then(object => {
        if (object) restoredObjects.push({ canvas, object })
      })
    })

    Promise.all(placements).then(() => {
      if (cancelled) return

      restoreCompleted = true
      isProgrammaticLayoutRestoreRef.current = false
      // Restoring suppresses mutation snapshots; seed history before the first edit.
      resetHistory()
      skipNextLayoutEmissionRef.current = true
      updateInitialRestoreStatus(RESTORE_COMPLETE)
      runBoundsCheck()
      setCanvasRevision(revision => revision + 1)
    })

    return () => {
      if (restoreCompleted) return
      cancelled = true
      restoredObjects.forEach(({ canvas, object }) => {
        if (canvas.getObjects?.().includes(object)) canvas.remove(object)
      })
      initialRestoreStatusRef.current = RESTORE_NOT_STARTED
      isProgrammaticLayoutRestoreRef.current = false
    }
  }, [
    imagePool,
    initialRestoreImagesLoaded,
    resetHistory,
    runBoundsCheck,
    sheets,
    updateInitialRestoreStatus,
  ])

  const emitLayoutChange = useCallback((force = false) => {
    if (!onLayoutChange) return
    if (isProgrammaticCanvasResizeRef.current || isProgrammaticLayoutRestoreRef.current) return

    const nextLayout = serializeLayout(layoutSheets, effectiveSheetSizeRef.current, canvasRefs.current)
    const serialized = JSON.stringify(nextLayout)
    if (!force && serialized === lastEmittedLayoutRef.current) return

    lastEmittedLayoutRef.current = serialized
    onLayoutChange(nextLayout)
  }, [layoutSheets, onLayoutChange])

  useEffect(() => {
    if (canvasRevision === 0) return
    if (skipNextLayoutEmissionRef.current) {
      skipNextLayoutEmissionRef.current = false
      return
    }

    const force = forceNextLayoutEmissionRef.current
    forceNextLayoutEmissionRef.current = false
    emitLayoutChange(force)
  }, [canvasRevision, emitLayoutChange])

  useEffect(() => {
    if (lastReportedSheetSizeIdRef.current === sheetSize.id) return

    lastReportedSheetSizeIdRef.current = sheetSize.id
    emitLayoutChange()
  }, [emitLayoutChange, sheetSize.id])

  const handleAddCopy = useCallback((objectId) => {
    const canvas = getActiveCanvas()
    if (!canvas) return
    const sourceObject = objectId
      ? canvas.getObjects().find(object => isSelectableCanvasObject(object) && object.objectId === objectId)
      : selectedCanvasObject
    if (!isPlacedArtworkObject(sourceObject)) return

    const imageId = sourceObject.uploadedImageId
    const sourceImage = images?.find(image => String(image.id) === String(imageId))
    const placedCount = countPlacedCopies(getPlacedObjects(), imageId)
    if (placedCount >= MAX_ARTWORK_QUANTITY) return
    setDropError(null)

    Promise.resolve().then(() => sourceObject.clone()).then(clone => {
      if (!clone) return
      clone.set({ left: Number(sourceObject.left) || 0, top: Number(sourceObject.top) || 0 })
      clone.setCoords?.()
      const currentSize = effectiveSheetSizeRef.current
      const copyPosition = adjacentCopyPosition(sourceObject, clone, canvas.getObjects(), currentSize) ||
        (isDynamicPricing && adjacentCopyPosition(sourceObject, clone, canvas.getObjects(), {
          ...currentSize, height_in: pricingSettings.max_height_inches,
        }))
      if (!copyPosition) {
        setDropError('No room for another copy on this sheet. Free up space or use Auto Arrange to rearrange your artwork, then try again. No copy was added.')
        return
      }
      startAtomicBatch(finishBatch => {
        isAtomicLayoutMutationRef.current = true
        clone.set({
          ...copyPosition,
          objectId: crypto.randomUUID(),
          uploadedImageId: imageId,
          quantity: 1,
        })
        clone.setCoords?.()
        canvas.add(clone)
        const activated = activateCanvasObject(canvas, clone)
        canvas.requestRenderAll()
        setSelectedCanvasObject(activated ? clone : null)
        updateImageConfig(imageId, previousConfig => (
          reconcileRequestedQuantityAfterAdd(previousConfig, placedCount, sourceImage)
        ))

        finishBatch()
        isAtomicLayoutMutationRef.current = false
        forceNextLayoutEmissionRef.current = true
        handleCanvasMutation()
      })
    }).catch(() => {})
  }, [
    getActiveCanvas,
    getPlacedObjects,
    handleCanvasMutation,
    images,
    isDynamicPricing,
    pricingSettings?.max_height_inches,
    selectedCanvasObject,
    startAtomicBatch,
    updateImageConfig,
  ])

  const handleRemoveCopy = useCallback((objectId) => {
    const canvas = getActiveCanvas()
    if (!canvas) return
    const targets = objectId
      ? canvas.getObjects().filter(object => (
          isPlacedArtworkObject(object) && object.objectId === objectId
        ))
      : selectedArtworkObjects(canvas.getActiveObject?.())
    if (targets.length === 0) return

    const placedObjects = getPlacedObjects()
    const removalsByImageId = new Map()
    targets.forEach(target => {
      const key = String(target.uploadedImageId)
      const removal = removalsByImageId.get(key) || {
        imageId: target.uploadedImageId,
        removedCount: 0,
      }
      removal.removedCount += 1
      removalsByImageId.set(key, removal)
    })

    startAtomicBatch(finishBatch => {
      isAtomicLayoutMutationRef.current = true
      const removedObjects = new Set(targets)
      if (selectionReferencesAnyObject(canvas.getActiveObject?.(), removedObjects)) {
        canvas.discardActiveObject?.()
      }
      targets.forEach(target => canvas.remove(target))
      canvas.requestRenderAll()
      if (targets.some(target => target === selectedCanvasObject || (
        target.objectId && target.objectId === selectedCanvasObject?.objectId
      ))) setSelectedCanvasObject(null)
      updateImageConfigs(previousConfigs => {
        const nextConfigs = { ...previousConfigs }
        removalsByImageId.forEach(({ imageId, removedCount }) => {
          const sourceImage = images?.find(image => String(image.id) === String(imageId))
          nextConfigs[imageId] = reconcileRequestedQuantityAfterRemove(
            previousConfigs[imageId],
            countPlacedCopies(placedObjects, imageId),
            sourceImage,
            removedCount
          )
        })
        return nextConfigs
      })

      pruneEmptySheetsAfterDeletion()
      finishBatch()
      isAtomicLayoutMutationRef.current = false
      forceNextLayoutEmissionRef.current = true
      handleCanvasMutation()
    })
  }, [
    getActiveCanvas,
    getPlacedObjects,
    handleCanvasMutation,
    images,
    pruneEmptySheetsAfterDeletion,
    selectedCanvasObject,
    startAtomicBatch,
    updateImageConfigs,
  ])

  // --- Delete key handler ---
  useEffect(() => {
    if (isHiddenCanvasMode) return undefined

    const handler = (e) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return
      const tag = document.activeElement?.tagName
      if (
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(tag) ||
        document.activeElement?.isContentEditable
      ) return
      handleRemoveCopy()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [handleRemoveCopy, isHiddenCanvasMode])

  // --- Undo/redo keyboard shortcuts ---
  useEffect(() => {
    if (isHiddenCanvasMode) return undefined

    const handler = (e) => {
      const isMac = (navigator.userAgentData?.platform || navigator.platform).toUpperCase().includes('MAC')
      const ctrl = isMac ? e.metaKey : e.ctrlKey
      if (!ctrl || e.key !== 'z') return
      e.preventDefault()
      if (e.shiftKey) {
        redo()
      } else {
        undo()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [isHiddenCanvasMode, undo, redo])

  const autofillImages = useMemo(() => (
    (arrangementImages || images || []).filter(
      img => img.processingStatus === 'processed' || img.processingStatus === undefined
    )
  ), [arrangementImages, images])
  const hasBackgroundProcessing = images?.some(img => img.backgroundProcessingStatus === 'processing') ?? false
  const hasBackgroundProcessingFailure = images?.some(img => img.backgroundProcessingStatus === 'failed') ?? false
  // One signal for "the artwork under the canvas is being rewritten": a removal
  // in flight, or the client adopting the geometry that removal produced.
  const editingFrozen = hasBackgroundProcessing || transitioning
  const allAutofillImagesLoaded = autofillImages.length > 0 &&
    autofillImages.every(img => loadedImageKeys.has(imageLoadKey(img)))
  const hasBackgroundRevisionPending = autofillImages.some(img => (
    Number(img.backgroundRevision ?? 0) > 0 && !loadedImageKeys.has(imageLoadKey(img))
  ))
  const backgroundUpdatePending = editingFrozen || hasBackgroundRevisionPending

  const isAutofillReady = allAutofillImagesLoaded &&
    !hasBackgroundProcessing &&
    !hasBackgroundProcessingFailure &&
    initialRestoreStatus === RESTORE_COMPLETE

  const contentSheetCount = useMemo(() => (
    sheets.filter(sheet => canvasHasContent(canvasRefs.current[sheet.id])).length
    // canvasRevision is the mutation signal for ref-held canvas content.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [sheets, canvasRevision])

  const consolidationAdvisory = useMemo(() => {
    if (isDynamicPricing) return null
    const { items } = buildAutoArrangePlan(autofillImages, [])
    return buildConsolidationAdvisory({
      items,
      sheetSizes: sortedSheetSizes,
      currentSheetSizeId: sheetSize.id,
      contentSheetCount,
      integrationMode: pricingSettings?.shopify_integration_mode ?? null,
    })
  }, [isDynamicPricing, autofillImages, sortedSheetSizes, sheetSize.id, contentSheetCount, pricingSettings?.shopify_integration_mode])

  const consolidationAdvisoryKey = consolidationAdvisory
    ? `${consolidationAdvisory.winner.id}:${consolidationAdvisory.sheetCount}`
    : null
  const [dismissedAdvisoryKey, setDismissedAdvisoryKey] = useState(null)
  const consolidationAdvisoryVisible = Boolean(
    consolidationAdvisory && dismissedAdvisoryKey !== consolidationAdvisoryKey && !isAutoArranging
  )
  const pendingConsolidationRef = useRef(null)

  const captureReviewState = useCallback(() => {
    const layout = serializeLayout(layoutSheets, effectiveSheetSize, canvasRefs.current)
    const thumbnails = sheets.map((sheet, index) => {
      const canvas = canvasRefs.current[sheet.id]
      if (!canvas) return null
      const renderedSheetSize = sheetSizeForIndex(index)
      const renderedCanvasWidthPx = renderedSheetSize.width_in * BASE_PPI
      const renderedCanvasHeightPx = renderedSheetSize.height_in * BASE_PPI
      const origZoom = canvas.getZoom()
      const origBg = canvas.backgroundColor
      const previousResizeState = isProgrammaticCanvasResizeRef.current
      isProgrammaticCanvasResizeRef.current = true
      try {
        canvas.setDimensions({ width: renderedCanvasWidthPx, height: renderedCanvasHeightPx })
        canvas.setZoom(1)
        canvas.backgroundColor = SHEET_SURFACE_COLOR
        canvas.renderAll?.()
        const thumbnailScale = getReviewThumbnailScale(
          renderedSheetSize,
          renderedCanvasWidthPx,
          renderedCanvasHeightPx
        )
        const exportCanvas = canvas.toCanvasElement?.(thumbnailScale)
        if (exportCanvas) {
          drawSafetyMarginOverlay(exportCanvas, renderedSheetSize, thumbnailScale)
          return exportCanvas.toDataURL('image/png')
        }
        return canvas.toDataURL({ format: 'png', multiplier: thumbnailScale })
      } catch {
        return null
      } finally {
        try {
          canvas.setDimensions({
            width: renderedCanvasWidthPx * origZoom,
            height: renderedCanvasHeightPx * origZoom,
          })
          canvas.setZoom(origZoom)
          canvas.backgroundColor = origBg
          canvas.renderAll?.()
          canvas.requestRenderAll()
        } finally {
          isProgrammaticCanvasResizeRef.current = previousResizeState
        }
      }
    })
    return { layout, thumbnails }
  }, [layoutSheets, sheets, effectiveSheetSize, sheetSizeForIndex])

  const handleContinue = useCallback(() => {
    const { layout, thumbnails } = captureReviewState()
    onContinue?.(layout, thumbnails)
  }, [captureReviewState, onContinue])

  // Manual Auto Arrange and guided Build use this single reconciliation and
  // Fabric placement operation. Guided callers skip confirmation and reject a
  // partial packing result before mutating the current canvases.
  const runAutoArrange = useCallback(({
    confirmExisting = true,
    rejectUnplaceable = false,
    freshPack = false,
    reactivateAvailableWhenEmpty = false,
    onFinished,
  } = {}) => {
    if (!isAutofillReady || isAutoArrangingRef.current) return

    const hasContent = sheets.some(sheet => canvasHasContent(canvasRefs.current[sheet.id]))
    if (confirmExisting && hasContent) {
      const confirmed = window.confirm('Auto Arrange will reposition artwork and match the requested quantities. Extra copies may be removed. Continue?')
      if (!confirmed) return
    }

    const sourceCounts = autofillImages.reduce((counts, image) => {
      const id = String(image.id)
      counts.set(id, (counts.get(id) || 0) + 1)
      return counts
    }, new Map())
    const reactivatedSourceIds = new Set()
    const arrangementImages = reactivateAvailableWhenEmpty && !hasContent
      ? autofillImages.map(image => {
          const config = normalizeArtworkConfig(image)
          if (config.included) return image

          const sourceId = String(image.id)
          reactivatedSourceIds.add(sourceId)
          return {
            ...image,
            quantity: sourceCounts.get(sourceId) > 1 ? config.quantity : 1,
            included: true,
          }
        })
      : autofillImages

    isAutoArrangingRef.current = true
    setIsAutoArranging(true)
    const operationGeneration = ++autoArrangeGenerationRef.current
    setOutsideSafety(false)
    setOutsideSheet(false)
    setDropError(null)

    const operationIsCurrent = () => (
      isAutoArrangingRef.current &&
      autoArrangeGenerationRef.current === operationGeneration
    )
    const failBeforeMutation = reason => {
      if (!operationIsCurrent()) return
      isAtomicLayoutMutationRef.current = false
      isAutoArrangingRef.current = false
      setIsAutoArranging(false)
      setDropError(reason)
      onFinished?.({ ok: false, reason })
    }

    let arrangementPlan
    let placements
    let unplaceable
    try {
      const repeatedSourceIds = new Set(arrangementImages.map(image => String(image.id))).size !== arrangementImages.length
      arrangementPlan = freshPack || repeatedSourceIds
        ? buildFreshPackPlan(arrangementImages, getPlacedObjects())
        : buildAutoArrangePlan(arrangementImages, getPlacedObjects())
      const result = autofill(
        arrangementPlan.items,
        autofillSheetSize,
        Math.max(arrangementPlan.items.length, 1)
      )
      placements = result.placements
      unplaceable = result.unplaceable
    } catch {
      failBeforeMutation('Could not arrange this artwork. Please try again.')
      return
    }
    setUnplaceableItems(unplaceable)
    if (rejectUnplaceable && unplaceable.length > 0) {
      const reason = 'Some requested artwork does not fit. Reduce its size or quantity and build again.'
      failBeforeMutation(reason)
      return
    }

    const existingByInstanceId = new Map(
      arrangementPlan.retained.map(({ object, instanceId }) => [instanceId, object])
    )
    const missingPlacements = placements.filter(placement => (
      !existingByInstanceId.has(placement.instance_id)
    ))
    const requiredSheetCount = Math.max(
      1,
      ...placements.map(placement => placement.sheet_number || 1)
    )
    let preparedCount = 0
    if (isGuidedBuilding) setBuildProgress(`Preparing ${placements.length} copies across ${requiredSheetCount} sheets…`)

    Promise.all(missingPlacements.map(placement => {
      const anchor = placementAnchorCoordinates(placement)
      return prepareImageClone(
        imagePool,
        placement.uploaded_image_id,
        anchor.x_in * BASE_PPI,
        anchor.y_in * BASE_PPI,
        placement.width_in,
        placement.height_in,
        {
          activate: false,
          objectId: placement.instance_id,
          quantity: 1,
          angle: placement.rotation_deg,
          flipX: placement.flip_x,
          flipY: placement.flip_y,
        }
      ).then(clone => {
        if (operationIsCurrent() && isGuidedBuilding) {
          preparedCount += 1
          setBuildProgress(`Preparing copies: ${preparedCount} of ${missingPlacements.length} · ${requiredSheetCount} sheets…`)
        }
        return clone
      })
    })).then(preparedClones => {
      if (!operationIsCurrent()) return
      if (preparedClones.some(clone => clone === null)) {
        failBeforeMutation('Could not place every requested copy. Please build again.')
        return
      }

      const operationStarted = startAtomicBatch(finishBatch => {
        const previousSheets = sheetsRef.current
        const selectedBeforeOperation = selectedCanvasObject
        const canvasBaselines = previousSheets.map(sheet => {
          const canvas = canvasRefs.current[sheet.id]
          const objects = canvas?.getObjects?.() || []
          return {
            canvas,
            activeObject: canvas?.getActiveObject?.() || null,
            objects: objects.map((object, zIndex) => ({
              object,
              zIndex,
              state: {
                left: object.left,
                top: object.top,
                scaleX: object.scaleX,
                scaleY: object.scaleY,
                quantity: object.quantity,
                angle: object.angle,
                flipX: object.flipX,
                flipY: object.flipY,
                objectId: object.objectId,
                uploadedImageId: object.uploadedImageId,
              },
            })),
          }
        })
        const targetSheets = Array.from({ length: requiredSheetCount }, (_, index) => (
          previousSheets[index] || {
            id: crypto.randomUUID(),
            label: `Sheet ${index + 1}`,
          }
        ))

        const removeSheetResources = removedSheets => {
          removedSheets.forEach(sheet => {
            canvasCleanupRefs.current[sheet.id]?.()
            delete canvasCleanupRefs.current[sheet.id]
            delete canvasReadyCache.current[sheet.id]
            delete canvasRefs.current[sheet.id]
          })
        }

        const setCurrentSheets = nextSheets => {
          sheetsRef.current = nextSheets
          setSheets(nextSheets)
        }

        const restoreBaseline = () => {
          targetSheets.slice(previousSheets.length).forEach(sheet => {
            const canvas = canvasRefs.current[sheet.id]
            canvas?.getObjects?.()
              .filter(isSelectableCanvasObject)
              .forEach(object => canvas.remove(object))
          })
          canvasBaselines.forEach(({ canvas, activeObject, objects }) => {
            if (!canvas) return
            const baselineObjects = new Set(objects.map(({ object }) => object))
            canvas.getObjects?.()
              .filter(object => !baselineObjects.has(object))
              .forEach(object => canvas.remove(object))
            objects.forEach(({ object, state, zIndex }) => {
              if (!canvas.getObjects?.().includes(object)) canvas.add(object)
              if (typeof object.set === 'function') object.set(state)
              else Object.assign(object, state)
              object.setCoords?.()
              canvas.moveObjectTo?.(object, zIndex)
            })
            canvas.discardActiveObject?.()
            if (activeObject) canvas.setActiveObject?.(activeObject)
            canvas.requestRenderAll?.()
          })
          const addedSheets = targetSheets.slice(previousSheets.length)
          setCurrentSheets(previousSheets)
          removeSheetResources(addedSheets)
          setActiveSheetIndex(Math.min(activeSheetIndexRef.current, previousSheets.length - 1))
          setSelectedCanvasObject(selectedBeforeOperation)
        }

        const waitForTargetCanvases = () => new Promise((resolve, reject) => {
          let attempts = 0
          const check = () => {
            if (!operationIsCurrent()) {
              reject(new Error('stale arrangement'))
              return
            }
            if (targetSheets.every(sheet => canvasRefs.current[sheet.id])) {
              resolve()
              return
            }
            attempts += 1
            if (attempts >= 120) {
              reject(new Error('canvas unavailable'))
              return
            }
            if (typeof window.requestAnimationFrame === 'function') {
              window.requestAnimationFrame(check)
            } else {
              window.setTimeout(check, 0)
            }
          }
          check()
        })

        if (requiredSheetCount > previousSheets.length) setCurrentSheets(targetSheets)

        waitForTargetCanvases().then(() => {
          if (!operationIsCurrent()) return
          isAtomicLayoutMutationRef.current = true
          const objectsToRemove = new Set(arrangementPlan.removals.map(({ object }) => object))
          const availableSheets = requiredSheetCount > previousSheets.length
            ? targetSheets
            : previousSheets
          availableSheets.forEach(sheet => {
            const canvas = canvasRefs.current[sheet.id]
            if (!canvas) return

            if (selectionReferencesAnyObject(canvas.getActiveObject?.(), objectsToRemove)) {
              canvas.discardActiveObject?.()
            }
            canvas.getObjects()
              .filter(object => objectsToRemove.has(object))
              .forEach(object => canvas.remove(object))
          })
          if ([...objectsToRemove].some(object => object === selectedCanvasObject || (
            object.objectId && object.objectId === selectedCanvasObject?.objectId
          ))) setSelectedCanvasObject(null)

          arrangementPlan.retained.forEach(({ object, instanceId }) => {
            if (typeof object.set === 'function') object.set({ objectId: instanceId, quantity: 1 })
            else Object.assign(object, { objectId: instanceId, quantity: 1 })
          })

          placements.forEach(placement => {
            const existingObject = existingByInstanceId.get(placement.instance_id)
            if (!existingObject) return
            const anchor = placementAnchorCoordinates(placement)

            const targetCanvas = canvasRefs.current[targetSheets[placement.sheet_number - 1].id]
            const sourceCanvas = availableSheets
              .map(sheet => canvasRefs.current[sheet.id])
              .find(canvas => canvas?.getObjects?.().includes(existingObject))
            if (sourceCanvas && sourceCanvas !== targetCanvas) {
              sourceCanvas.remove(existingObject)
              targetCanvas.add(existingObject)
            }

            existingObject.set?.({
              left: anchor.x_in * BASE_PPI,
              top: anchor.y_in * BASE_PPI,
              quantity: 1,
              angle: placement.rotation_deg ?? existingObject.angle,
              flipX: placement.flip_x ?? existingObject.flipX,
              flipY: placement.flip_y ?? existingObject.flipY,
            })
            existingObject.setCoords?.()
          })

          missingPlacements.forEach((placement, index) => {
            const canvas = canvasRefs.current[targetSheets[placement.sheet_number - 1].id]
            const clone = preparedClones[index]
            canvas.add(clone)
            bringSafetyMarginToFront(canvas)
          })
          targetSheets.forEach(sheet => canvasRefs.current[sheet.id]?.requestRenderAll?.())

          const removedSheets = previousSheets.slice(requiredSheetCount)
          if (removedSheets.length > 0) {
            setCurrentSheets(targetSheets)
            removeSheetResources(removedSheets)
          }

          const selectedInstanceId = selectedBeforeOperation?.objectId
          const selectedPlacement = placements.find(placement => (
            placement.instance_id === selectedInstanceId
          ))
          const nextActiveIndex = selectedPlacement
            ? selectedPlacement.sheet_number - 1
            : Math.min(activeSheetIndexRef.current, targetSheets.length - 1)
          setActiveSheetIndex(nextActiveIndex)
          setSelectedCanvasObject(selectedPlacement ? selectedBeforeOperation : null)
          if (targetSheets.length > 1) {
            setSheetAnnouncement(
              `Auto Arrange created ${targetSheets.length} sheets. ` +
              `Viewing Sheet ${nextActiveIndex + 1} of ${targetSheets.length}. ` +
              'Use the sheet navigation to review every sheet.'
            )
          }

          if (reactivatedSourceIds.size > 0) {
            updateImageConfigs(previousConfigs => {
              const nextConfigs = { ...previousConfigs }
              autofillImages.forEach(image => {
                if (!reactivatedSourceIds.has(String(image.id))) return

                nextConfigs[image.id] = normalizeArtworkConfig({
                  ...(previousConfigs[image.id] || {}),
                  ...(sourceCounts.get(String(image.id)) > 1 ? {} : { quantity: 1 }),
                  included: true,
                }, image)
              })
              return nextConfigs
            })
          }
          finishBatch()
          isAtomicLayoutMutationRef.current = false
          isAutoArrangingRef.current = false
          setIsAutoArranging(false)
          forceNextLayoutEmissionRef.current = true
          handleCanvasMutation()
          onFinished?.({ ok: true })
        }).catch(() => {
          // Timeout/unmount already disposed this operation's canvases. Do not
          // restore them or report a stale failure into a newer build.
          if (!operationIsCurrent()) return
          restoreBaseline()
          finishBatch(false)
          isAtomicLayoutMutationRef.current = false
          isAutoArrangingRef.current = false
          setIsAutoArranging(false)
          const reason = 'Could not place every requested copy. Please build again.'
          setDropError(reason)
          onFinished?.({ ok: false, reason })
        })
      })

      if (!operationStarted) {
        failBeforeMutation('Could not start the arrangement. Please try again.')
      }
    }).catch(() => {
      failBeforeMutation('Could not place every requested copy. Please build again.')
    })
  }, [
    autofillImages,
    isAutofillReady,
    sheets,
    autofillSheetSize,
    getPlacedObjects,
    handleCanvasMutation,
    imagePool,
    selectedCanvasObject,
    startAtomicBatch,
    updateImageConfigs,
    isGuidedBuilding,
  ])

  const handleAutofill = useCallback(() => {
    runAutoArrange({ reactivateAvailableWhenEmpty: true })
  }, [runAutoArrange])

  const handleAcceptConsolidation = useCallback(() => {
    if (!consolidationAdvisory) return
    pendingConsolidationRef.current = consolidationAdvisory.winner.id
    onSelectedSheetSizeIdChange(consolidationAdvisory.winner.id)
  }, [consolidationAdvisory, onSelectedSheetSizeIdChange])

  useEffect(() => {
    const pendingId = pendingConsolidationRef.current
    if (pendingId == null) return
    pendingConsolidationRef.current = null
    if (Number(sheetSize.id) !== Number(pendingId)) return  // manual switch raced the accept

    runAutoArrange({
      freshPack: true,
      confirmExisting: false,
      onFinished: result => {
        if (result?.ok === false) return  // existing error path already surfaced it
        // The candidate is only offered when it packs everything onto one
        // sheet, but autofillImages can change between the advisory memo and
        // this async completion — so don't assume; check the real outcome.
        // sheetsRef.current is updated synchronously by setCurrentSheets,
        // ahead of the setSheets() render this callback runs after.
        if (sheetsRef.current.length !== 1) return  // multi-sheet path already announced itself above
        // Arm the suppression flag synchronously, in the same tick as the
        // sheets-collapse-to-one that just happened, so the sheets.length<=1
        // cleanup effect (queued but not yet flushed) skips this announcement.
        suppressSheetCountAnnouncementClearRef.current = true
        setSheetAnnouncement(`Switched to ${sheetSize.label} — everything fits on one sheet`)
      },
    })
    // runAutoArrange identity churns per render; the pending ref gates real work.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheetSize.id])

  useEffect(() => {
    if (guidedBuildRequestId === undefined || guidedBuildRequestId === null) return
    if (!isAutofillReady || isAutoArrangingRef.current) return
    if (handledGuidedBuildRequestRef.current === guidedBuildRequestId) return

    handledGuidedBuildRequestRef.current = guidedBuildRequestId
    runAutoArrange({
      confirmExisting: false,
      rejectUnplaceable: true,
      onFinished: result => {
        if (result.ok) setGuidedCaptureRequestId(guidedBuildRequestId)
        else onGuidedBuildError?.(result.reason)
      },
    })
  }, [guidedBuildRequestId, isAutofillReady, onGuidedBuildError, runAutoArrange])

  useEffect(() => {
    if (guidedCaptureRequestId === null) return
    if (completedGuidedBuildRequestRef.current === guidedCaptureRequestId) return

    completedGuidedBuildRequestRef.current = guidedCaptureRequestId
    setGuidedCaptureRequestId(null)
    setBuildProgress('Preparing your preview and saving the layout…')
    handleContinue()
  }, [guidedCaptureRequestId, handleContinue])

  useEffect(() => {
    if (reviewPreviewRequestId === undefined || reviewPreviewRequestId === null) return
    if (initialRestoreStatus !== RESTORE_COMPLETE || isAutoArrangingRef.current) return
    if (handledReviewPreviewRequestRef.current === reviewPreviewRequestId) return

    const generation = ++reviewPreviewCaptureGenerationRef.current
    let cancelled = false
    const isStale = () => (
      cancelled ||
      reviewPreviewCaptureGenerationRef.current !== generation
    )
    const waitForPaint = (delayMs = 0) => new Promise(resolve => {
      if (delayMs > 0) {
        window.setTimeout(resolve, delayMs)
      } else if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(() => window.requestAnimationFrame(resolve))
      } else {
        window.setTimeout(resolve, 0)
      }
    })
    const hasUsableThumbnails = captured => {
      const expectedSheetCount = captured.layout?.sheets?.length || 0
      return expectedSheetCount > 0 &&
        captured.thumbnails.length >= expectedSheetCount &&
        captured.thumbnails.slice(0, expectedSheetCount).every(thumbnail => (
          typeof thumbnail === 'string' &&
          thumbnail.length > 'data:image/png;base64,'.length &&
          thumbnail !== 'data:,'
        ))
    }

    const captureAfterRestorePaint = async () => {
      const retryDelays = [0, 75, 150]
      for (const retryDelay of retryDelays) {
        await waitForPaint(retryDelay)
        if (isStale()) return

        try {
          const captured = captureReviewState()
          if (!hasUsableThumbnails(captured)) continue

          handledReviewPreviewRequestRef.current = reviewPreviewRequestId
          onReviewPreviewCaptured?.(captured.layout, captured.thumbnails)
          return
        } catch {
          // A restored Fabric canvas may need another paint before it can export.
        }
      }

      if (isStale()) return
      handledReviewPreviewRequestRef.current = reviewPreviewRequestId
      onReviewPreviewError?.('Could not prepare the design preview. Return to the layout and try again.')
    }

    captureAfterRestorePaint()
    return () => {
      cancelled = true
    }
  }, [
    captureReviewState,
    initialRestoreStatus,
    onReviewPreviewCaptured,
    onReviewPreviewError,
    reviewPreviewRequestId,
  ])

  // --- Drag handlers ---
  const handleDragOver = useCallback((e) => {
    e.preventDefault()
  }, [])

  const dragCounterRef = useRef(0)
  const handleDragEnter = useCallback(() => {
    dragCounterRef.current++
    setDragOver(true)
  }, [])
  const handleDragLeave = useCallback(() => {
    dragCounterRef.current--
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0
      setDragOver(false)
    }
  }, [])

  const handlePlaceSourceImage = useCallback((
    uploadedImageId,
    dropX,
    dropY,
    printWidthIn,
    printHeightIn
  ) => {
    const canvas = getActiveCanvas()
    const sourceImage = images?.find(image => String(image.id) === String(uploadedImageId))
    if (!canvas || !sourceImage) return

    const placedCount = countPlacedCopies(getPlacedObjects(), uploadedImageId)
    if (placedCount >= MAX_ARTWORK_QUANTITY) return

    startAtomicBatch(finishBatch => {
      isAtomicLayoutMutationRef.current = true
      placeImage(
        imagePool,
        uploadedImageId,
        canvas,
        dropX,
        dropY,
        printWidthIn,
        printHeightIn
      ).then(object => {
        if (object) {
          updateImageConfig(uploadedImageId, previousConfig => (
            reconcileRequestedQuantityAfterPlacement(previousConfig, placedCount, sourceImage)
          ))
          setSelectedCanvasObject(canvas.getActiveObject?.() === object ? object : null)
        }

        finishBatch(Boolean(object))
        isAtomicLayoutMutationRef.current = false
        if (object) {
          forceNextLayoutEmissionRef.current = true
          handleCanvasMutation()
        }
      })
    })
  }, [
    getActiveCanvas,
    getPlacedObjects,
    handleCanvasMutation,
    imagePool,
    images,
    startAtomicBatch,
    updateImageConfig,
  ])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    dragCounterRef.current = 0
    setDragOver(false)
    if (e.dataTransfer?.files?.length) {
      uploadFiles?.(e.dataTransfer.files)
      return
    }
    const uploadedImageId = e.dataTransfer.getData('uploadedImageId')
    if (!uploadedImageId) return
    if (!imagePool.has(String(uploadedImageId))) return
    if (!canvasWrapperRef.current || !getActiveCanvas()) return

    // Get print dimensions from image props (set by vendor or autofill)
    const image = images?.find(img => String(img.id) === String(uploadedImageId))
    if (!image?.printWidthIn || !image?.printHeightIn) {
      setDropError('Set print width and height before placing artwork, or use Add more artwork to update dimensions.')
      return
    }
    setDropError(null)

    const rect = canvasWrapperRef.current.getBoundingClientRect()
    const dropX = (e.clientX - rect.left)  / zoom
    const dropY = (e.clientY - rect.top)   / zoom

    handlePlaceSourceImage(
      uploadedImageId,
      dropX,
      dropY,
      image.printWidthIn,
      image.printHeightIn
    )
  }, [uploadFiles, imagePool, images, zoom, getActiveCanvas, handlePlaceSourceImage])

  // --- Continue button: blocking state (derived, no useState needed) ---
  const hasAnyPlacement = sheets.some(sheet => {
    const canvas = canvasRefs.current[sheet.id]
    return canvas ? canvas.getObjects().some(o => o.name !== SAFETY_MARGIN_OBJECT_NAME) : false
  })

  const hasPendingImages = imagesArePending(images)
  const hasFailedImages  = images?.some(img => img.processingStatus === 'failed')  ?? false

  const dynamicHeightBlocked = Boolean(dynamicQuote?.exceedsMaximum || dynamicQuote?.tooManySheets)
  const dynamicBoundsBlocked = Boolean(dynamicQuote?.hasInvalidBounds)
  const currentLayoutForWarnings = useMemo(() => (
    serializeLayout(layoutSheets, effectiveSheetSize, canvasRefs.current)
  ), [layoutSheets, effectiveSheetSize, canvasRevision])
  const overlappingSheetNumber = useMemo(
    () => firstOverlappingSheetNumber(currentLayoutForWarnings),
    [currentLayoutForWarnings]
  )
  const artworkQualityWarnings = useMemo(() => (
    buildArtworkQualityWarnings(currentLayoutForWarnings, images)
  ), [currentLayoutForWarnings, images])
  const isContinueBlocked = !hasAnyPlacement || hasPendingImages || hasFailedImages ||
    editingFrozen || hasBackgroundProcessingFailure ||
    hasBackgroundRevisionPending ||
    dynamicHeightBlocked || dynamicBoundsBlocked || Boolean(overlappingSheetNumber)
  const continueBlockReason = !hasAnyPlacement
    ? 'Click Auto Arrange or drag artwork onto the sheet first'
    : hasPendingImages
    ? 'Wait for all images to finish processing'
    : hasFailedImages
    ? 'Remove failed images before continuing'
    : editingFrozen
    ? 'Wait for background processing to finish'
    : hasBackgroundProcessingFailure
    ? 'Review the failed background removal before continuing'
    : hasBackgroundRevisionPending
    ? 'Wait for the updated artwork to finish loading'
    : dynamicHeightBlocked
    ? dynamicQuote?.tooManySheets
      ? 'Split this job into two orders (5 sheets per order).'
      : dynamicQuote?.missingTier
        ? 'This sheet is taller than the largest size available.'
        : `Artwork exceeds the maximum sheet height of ${pricingSettings.max_height_inches} inches`
    : dynamicBoundsBlocked
    ? 'Keep all artwork within the fixed sheet width and above the top edge'
    : overlappingSheetNumber
    ? `Move overlapping artwork apart on Sheet ${overlappingSheetNumber}`
    : ''

  // --- Requested-versus-placed quantity warning ---
  const placementCounts = {}
  sheets.forEach(sheet => {
    const canvas = canvasRefs.current[sheet.id]
    if (!canvas) return
    canvas.getObjects()
      .filter(o => o.name !== SAFETY_MARGIN_OBJECT_NAME && o.uploadedImageId)
      .forEach(o => {
        placementCounts[o.uploadedImageId] = (placementCounts[o.uploadedImageId] || 0) + 1
      })
  })
  const hasPlacementMismatch = images?.some(img => {
    const placed = placementCounts[img.id] || 0
    const requested = effectiveRequestedQuantity(imageConfigs[img.id], img)
    return placed !== requested
  }) ?? false
  const showInitialUploadPrompt = canUpload && (images?.length ?? 0) === 0 && !hasAnyPlacement

  const handleAddArtwork = useCallback(() => {
    if (canUpload) {
      openFilePicker()
      return
    }

    const layout = serializeLayout(layoutSheets, effectiveSheetSize, canvasRefs.current)

    if (onAddArtwork) {
      onAddArtwork(layout)
    } else {
      onBack?.()
    }
  }, [canUpload, openFilePicker, layoutSheets, effectiveSheetSize, onAddArtwork, onBack])

  const handleStartNew = useCallback(async () => {
    if (isStartingNewRef.current) return

    const confirmed = window.confirm(
      'Start a new gang sheet? Your current draft will be closed and the canvas will be cleared.'
    )
    if (!confirmed) return

    isStartingNewRef.current = true
    setIsStartingNew(true)
    setStartNewError(null)
    try {
      await onStartNew()
    } catch (error) {
      setStartNewError(error?.message || 'Could not start a new gang sheet. Please try again.')
    } finally {
      isStartingNewRef.current = false
      setIsStartingNew(false)
    }
  }, [onStartNew])

  const selectedArtwork = useMemo(() => {
    if (!isSelectableCanvasObject(selectedCanvasObject)) return null
    return images?.find(image => String(image.id) === String(selectedCanvasObject.uploadedImageId)) || null
  }, [images, selectedCanvasObject])

  const customerPortalHref = customerPortalPath || `/builder/${vendorToken}/customer`

  if (isGuidedBuilding) {
    return (
      <main className="guided-building" data-testid="guided-building-screen" aria-live="polite">
        <div>
          <div className="guided-building__spinner" aria-hidden="true" />
          <h1>Building your gang sheet…</h1>
          <p role="status">{buildProgress}</p>
        </div>
        <div className="guided-building__canvas-host" ref={canvasContainerRef} aria-hidden="true">
          <div ref={canvasWrapperRef}>
            {sheets.map((sheet, index) => (
              <SheetCanvas
                key={sheet.id}
                sheetSize={sheetSizeForIndex(index)}
                zoom={zoom}
                isActive={index === activeSheetIndex}
                frozen={editingFrozen}
                onCanvasReady={getCanvasReadyHandler(sheet.id)}
                isProgrammaticCanvasResizeRef={isProgrammaticCanvasResizeRef}
                showSafetyMargin
                showTransparencyGrid
              />
            ))}
          </div>
        </div>
      </main>
    )
  }

  return (
    <>
      {isReviewPreviewCapturing && (
        <main className="guided-building guided-building--overlay" data-testid="review-preview-building-screen" aria-live="polite">
          <div>
            <div className="guided-building__spinner" aria-hidden="true" />
            <h1>Preparing your preview…</h1>
            <p>We’re restoring the saved layout for your review.</p>
          </div>
        </main>
      )}
      <div
        className="editor-layout"
        data-canvas-revision={canvasRevision}
        aria-hidden={isReviewPreviewCapturing ? 'true' : undefined}
        inert={isReviewPreviewCapturing}
      >
      <input
        ref={fileInputRef}
        type="file"
        data-testid="canvas-file-input"
        accept=".png,.jpg,.jpeg,.webp"
        multiple
        style={{ display: 'none' }}
        onClick={(e) => e.stopPropagation()}
        onChange={handleUploadInputChange}
      />
      {/* Builder warning and error banners */}
      {loadError && (
        <BuilderAlert tone="error" title="Couldn’t load this gang sheet">
          {loadError}
        </BuilderAlert>
      )}
      {startNewError && (
        <BuilderAlert tone="error" title="Couldn’t start a new gang sheet" data-testid="start-new-error">
          {startNewError}
        </BuilderAlert>
      )}
      {deleteArtworkError && (
        <BuilderAlert tone="error" title="Couldn’t remove artwork" data-testid="delete-artwork-error">
          {deleteArtworkError}
        </BuilderAlert>
      )}
      {outsideSafety && !outsideSheet && (
        <BuilderAlert
          title="Placement needs attention"
          actionLabel={isAutoArranging ? 'Arranging…' : 'Auto Arrange now'}
          actionDisabled={!isAutofillReady || isAutoArranging}
          onAction={handleAutofill}
          data-testid="warning-outside-safety"
        >
          Move artwork inside the dashed safe area, or click Auto Arrange to place it for you.
        </BuilderAlert>
      )}
      {outsideSheet && (
        <BuilderAlert
          tone="error"
          title="Artwork is outside the sheet"
          actionLabel={isAutoArranging ? 'Arranging…' : 'Auto Arrange now'}
          actionDisabled={!isAutofillReady || isAutoArranging}
          onAction={handleAutofill}
          data-testid="warning-outside-sheet"
        >
          Move artwork fully onto the sheet, or click Auto Arrange to fit what can be placed.
        </BuilderAlert>
      )}
      {dynamicHeightBlocked && (
        <BuilderAlert tone="error" title="Artwork exceeds the sheet limit" data-testid="warning-dynamic-max-height">
          {dynamicQuote?.tooManySheets ? (
            'Split this job into two orders (5 sheets per order).'
          ) : dynamicQuote?.missingTier ? (
            'This sheet is taller than the largest size available.'
          ) : (
            <>
              Artwork exceeds the maximum automatic-height sheet length of {formatInches(pricingSettings.max_height_inches)} inches.
              Move, resize, or remove artwork before continuing.
            </>
          )}
        </BuilderAlert>
      )}
      {dropError && (
        <BuilderAlert tone="error" title="Artwork couldn’t be placed" data-testid="warning-drop-error">
          {dropError}
        </BuilderAlert>
      )}
      {canvasTransformError && (
        <BuilderAlert tone="error" title="That placement won’t fit" data-testid="warning-canvas-transform-error">
          {canvasTransformError}
        </BuilderAlert>
      )}
      {overlappingSheetNumber && (
        <BuilderAlert tone="error" title="Artwork overlaps" data-testid="warning-overlapping-artwork">
          Move the overlapping images apart on Sheet {overlappingSheetNumber} before reviewing your design.
        </BuilderAlert>
      )}
      {backgroundUpdatePending && (
        <BuilderAlert title="Updating artwork" data-testid="background-update-status">
          Background removal is finishing. Auto Arrange and artwork placement will unlock automatically.
        </BuilderAlert>
      )}
      {hasPlacementMismatch && !backgroundUpdatePending && (
        <BuilderAlert
          title="Copies need attention"
          actionLabel={isAutoArranging ? 'Arranging…' : 'Auto Arrange now'}
          actionDisabled={!isAutofillReady || isAutoArranging}
          onAction={handleAutofill}
          data-testid="warning-partial-qty"
        >
          Placed copies do not match the requested quantities. Click Auto Arrange to reconcile the sheet.
        </BuilderAlert>
      )}
      {artworkQualityWarnings.length > 0 && (
        <BuilderAlert
          tone="warning"
          title="Low DPI detected"
          data-testid="warning-artwork-quality"
        >
          {ARTWORK_QUALITY_WARNING_COPY}
          {' '}
          {artworkQualityWarnings.length === 1
            ? `${artworkQualityWarnings[0].imageName}: ${artworkQualityWarnings[0].dpi} DPI.`
            : `${artworkQualityWarnings.length} placed images are below 300 DPI.`}
        </BuilderAlert>
      )}
      {unplaceableItems.length > 0 && (
        <BuilderAlert title="Some artwork was not placed" data-testid="warning-unplaceable">
          {unplaceableItems.length} image(s) could not be placed: {
            [...new Set(unplaceableItems.map(u => u.reason))].map(reason => {
              if (reason === 'invalid_dimensions') return 'set print dimensions for all images, then click Auto Arrange'
              if (reason === 'too_wide') return 'reduce image width or choose a wider sheet'
              if (reason === 'too_tall') return 'reduce image height or choose a taller sheet'
              if (reason === 'sheet_limit') return 'some images do not fit on this sheet; reduce quantity or image size, then click Auto Arrange again'
              return reason
            }).join('; ')
          }
        </BuilderAlert>
      )}

      <header
        className={`editor-toolbar${isDynamicPricing ? ' editor-toolbar--dynamic' : ''}`}
        data-testid="builder-workflow-toolbar"
      >
        <div className="editor-toolbar__navigation">
          {(canUpload || onAddArtwork || onBack) && (
            <button
              className="editor-back-btn"
              onClick={handleAddArtwork}
              title="Upload more artwork without clearing your current layout"
            >
              Add more artwork
            </button>
          )}
          {onStartNew && (
            <button
              className="editor-back-btn editor-start-new-btn"
              type="button"
              data-testid="start-new-btn"
              title="Close this draft and start a new gang sheet"
              onClick={handleStartNew}
              disabled={isStartingNew}
            >
              {isStartingNew ? 'Starting…' : 'Start New'}
            </button>
          )}
          <a className="editor-customer-portal-link" href={customerPortalHref} target="_top">
            View your gang sheets
          </a>
        </div>

        <div className="editor-toolbar__status">
          {!isDynamicPricing && sortedSheetSizes.length > 0 && (
            <label className="editor-sheet-size-select">
              <span>Sheet</span>
              <select
                value={selectedSheetSizeId ?? sheetSize.id ?? ''}
                onChange={(e) => onSelectedSheetSizeIdChange?.(Number(e.target.value))}
              >
                {sortedSheetSizes.map(size => (
                  <option key={size.id} value={size.id}>{size.label}</option>
                ))}
              </select>
            </label>
          )}
          {isDynamicPricing && <DynamicPriceSummary quote={dynamicQuote} />}
          <SaveStatusIndicator status={saveStatus} lastAutosavedAt={lastAutosavedAt} />
        </div>

        <div className="editor-toolbar__primary-actions">
          <button
            className="editor-autofill-btn"
            data-testid="autofill-btn"
            onClick={handleAutofill}
            disabled={!isAutofillReady || isAutoArranging}
            title={isAutoArranging
              ? 'Auto Arrange is running'
              : editingFrozen
              ? 'Wait for background removal to finish'
              : hasBackgroundRevisionPending
              ? 'Loading the updated artwork'
              : isAutofillReady
              ? hasAnyPlacement
                ? 'Recalculate the sheet with Auto Arrange'
                : 'Automatically arrange artwork inside the safe area'
              : 'Preparing artwork for Auto Arrange'}
          >
            {isAutoArranging ? 'Arranging…' : hasAnyPlacement ? 'Auto Arrange again' : 'Auto Arrange'}
          </button>
          <button
            className="editor-continue-btn"
            data-testid="continue-btn"
            disabled={isContinueBlocked}
            title={continueBlockReason}
            onClick={handleContinue}
          >
            Review design →
          </button>
        </div>
      </header>

      <div
        className="editor-edit-toolbar"
        role="toolbar"
        aria-label="Canvas editing controls"
        data-testid="canvas-editing-toolbar"
      >
        <div className="editor-toolbar__undo-redo">
          <button onClick={undo} disabled={!canUndo} data-testid="undo-btn" aria-label="Undo">↩</button>
          <button onClick={redo} disabled={!canRedo} data-testid="redo-btn" aria-label="Redo">↪</button>
        </div>
        <SelectionToolbar
          canvas={getActiveCanvas()}
          onChange={handleCanvasMutation}
          onSelectionChange={handleSelectionChange}
          onAddCopy={handleAddCopy}
          onRemoveCopy={handleRemoveCopy}
          sheetSize={effectiveSheetSize}
        />
        <div className="editor-edit-toolbar__spacer" />
        <label className="editor-grid-toggle" title="Editor-only background and placement guides. Does not print or snap artwork.">
          <input
            type="checkbox"
            checked={showGrid}
            onChange={event => setShowGrid(event.target.checked)}
          />
          Show grid
        </label>
        <ZoomControls zoom={zoom} onZoom={updateZoom} onFit={handleFit} />
        {compactEditor && (
          <div className="editor-navigation-mode">
            <div role="group" aria-label="Canvas interaction mode">
              <button type="button" aria-pressed={!mobileEditing} onClick={() => setMobileEditing(false)}>
                Move around
              </button>
              <button type="button" aria-pressed={mobileEditing} onClick={() => setMobileEditing(true)}>
                Edit artwork
              </button>
            </div>
            <span>{navigationOnly ? 'Swipe to scroll without moving artwork.' : 'Drag to edit. Choose Move around to scroll.'}</span>
          </div>
        )}
      </div>

      <div className="editor-body">
        <Sidebar
          compact={compactEditor}
          images={images}
          imageUrls={imageUrls}
          activeCanvas={getActiveCanvas()}
          placementCounts={placementCounts}
          activeSheetNumber={activeSheetIndex + 1}
          editingDisabled={editingFrozen || backgroundUpdatePending || isAutoArranging}
          sheetSize={sheetSize}
          uploadErrors={uploadErrors}
          uploadingFiles={uploadingFiles}
          imageConfigs={imageConfigs}
          selectedSheetSize={artworkConstraintSheetSize}
          onOpenUploadPicker={canUpload ? openFilePicker : null}
          onUploadFiles={canUpload ? uploadFiles : null}
          onImageConfigChange={handleImageConfigChange}
          onRemoveImage={canUpload ? handleRemoveImage : null}
          onRetryImage={canUpload ? handleRetryImage : null}
          selectedArtwork={selectedArtwork}
          selectedCanvasObject={selectedCanvasObject}
          onArtworkInstanceConfigChange={handleArtworkInstanceConfigChange}
          onAddCopy={handleAddCopy}
          onRemoveCopy={handleRemoveCopy}
          onSelectCanvasObject={handleSelectCanvasObject}
          onPlaceImage={handlePlaceSourceImage}
          deletingImageIds={deletingImageIds}
          onBackgroundPreview={onBackgroundPreview}
          onBackgroundApply={onBackgroundApply}
          vendorArtworks={vendorArtworks}
          vendorArtworksLoading={vendorArtworksLoading}
          vendorArtworksError={vendorArtworksError}
          shopDesignsSearch={shopDesignsSearch}
          onImportVendorArtwork={onImportVendorArtwork}
          maxImageCount={maxImageCount}
        />

        <div className="editor-canvas-area" ref={canvasContainerRef}>
          {sheetAnnouncement && (
            <div
              className="editor-sheet-announcement"
              role="status"
              aria-live="polite"
              aria-atomic="true"
              data-testid="sheet-announcement"
            >
              {sheetAnnouncement}
            </div>
          )}
          {consolidationAdvisoryVisible && (
            <aside
              className="editor-consolidation-advisory"
              aria-labelledby="consolidation-advisory-title"
              data-testid="consolidation-advisory"
            >
              <p id="consolidation-advisory-title" className="editor-consolidation-advisory__title">
                {CONSOLIDATION_ADVISORY_TITLE}
              </p>
              <p className="editor-consolidation-advisory__copy">
                {consolidationAdvisoryCopy(consolidationAdvisory)}
              </p>
              <div className="editor-consolidation-advisory__actions">
                <button
                  type="button"
                  className="editor-consolidation-advisory__accept"
                  onClick={handleAcceptConsolidation}
                  disabled={isAutoArranging || !isAutofillReady}
                >
                  Switch &amp; re-arrange
                </button>
                <button
                  type="button"
                  className="editor-consolidation-advisory__dismiss"
                  onClick={() => setDismissedAdvisoryKey(consolidationAdvisoryKey)}
                >
                  Dismiss
                </button>
              </div>
            </aside>
          )}
          <div className="editor-canvas-content">
            {sheets.length > 1 && (
              <div className="editor-sheet-navigation" ref={sheetNavigationRef}>
                <nav className="editor-sheet-tabs" aria-label="Gang sheet pages">
                  {sheets.map((sheet, index) => (
                    <button
                      key={sheet.id}
                      type="button"
                      className="editor-sheet-tab"
                      aria-current={index === activeSheetIndex ? 'page' : undefined}
                      onClick={() => handleSelectSheet(index)}
                    >
                      Sheet {index + 1}
                      <span>{formatInches(sheetSizeForIndex(index).height_in)}″</span>
                    </button>
                  ))}
                </nav>
                <nav className="editor-sheet-navigator" aria-label="Current sheet navigation" data-testid="sheet-navigator">
                  <p className="editor-sheet-navigator__status" aria-atomic="true">
                    <strong>{sheets.length} sheets</strong>
                    <span aria-hidden="true"> · </span>
                    <span>Viewing Sheet {activeSheetIndex + 1} of {sheets.length}</span>
                  </p>
                  <div className="editor-sheet-navigator__actions">
                    <button
                      type="button"
                      onClick={() => handleSelectSheet(activeSheetIndex - 1)}
                      disabled={activeSheetIndex === 0}
                      aria-label="View previous sheet"
                    >
                      ← Previous
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSelectSheet(activeSheetIndex + 1)}
                      disabled={activeSheetIndex === sheets.length - 1}
                      aria-label="View next sheet"
                    >
                      Next →
                    </button>
                  </div>
                </nav>
              </div>
            )}
            <div className="editor-canvas-with-rulers">
              {/* Top-left corner filler */}
              <div className="editor-ruler-corner" />

              {/* Horizontal ruler */}
              <SheetRuler
                direction="horizontal"
                lengthPx={canvasWidthPx}
                zoom={zoom}
              />

              {/* Vertical ruler */}
              <SheetRuler
                direction="vertical"
                lengthPx={canvasHeightPx}
                zoom={zoom}
              />

              {/* Canvas drop target — contains all sheet canvases */}
              <div
                ref={canvasWrapperRef}
                className={`editor-canvas-cell${dragOver ? ' editor-canvas-area--drag-over' : ''}`}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                data-testid="canvas-drop-target"
              >
                {sheets.map((sheet, index) => (
                  <div
                    key={sheet.id}
                    className="sheet-canvas-wrapper"
                    data-active={index === activeSheetIndex}
                  >
                    {index === activeSheetIndex && (showInitialUploadPrompt ? (
                      <div
                        className={`editor-empty-upload-prompt${uploadDragOver ? ' editor-empty-upload-prompt--drag-over' : ''}`}
                        data-testid="empty-canvas-upload-prompt"
                        onDrop={handleUploadDrop}
                        onDragEnter={handleUploadDragEnter}
                        onDragOver={handleUploadDragOver}
                        onDragLeave={handleUploadDragLeave}
                      >
                        <h2>Upload artwork to start your gang sheet</h2>
                        <p>Drag files here or use the upload button.</p>
                        <button type="button" onClick={openFilePicker}>
                          Upload artwork
                        </button>
                        <span>PNG, JPEG, WebP · Max 100 MB</span>
                      </div>
                    ) : !hasAnyPlacement && (
                      <div className="editor-empty-canvas-guidance" data-testid="empty-canvas-guidance">
                        Drag artwork here, or click Auto Arrange.
                      </div>
                    ))}
                    <SheetCanvas
                      sheetSize={sheetSizeForIndex(index)}
                      maxArtworkHeightIn={isDynamicPricing ? pricingSettings.max_height_inches : undefined}
                      zoom={zoom}
                      isActive={index === activeSheetIndex}
                      frozen={editingFrozen}
                      navigationOnly={navigationOnly}
                      onCanvasReady={getCanvasReadyHandler(sheet.id)}
                      onTransformValidationChange={setCanvasTransformError}
                      isProgrammaticCanvasResizeRef={isProgrammaticCanvasResizeRef}
                      showSafetyMargin
                      showTransparencyGrid={showGrid}
                      showPlacementGrid={showGrid}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </>
  )
}
